import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Upload,
  Plus,
  Play,
  Pause,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Info,
  FileText,
  Settings,
  ChevronRight,
  Loader2,
  Calendar,
  Database,
  Sparkles,
  Search as SearchIcon,
  FolderTree,
  History,
  Eye,
  Pencil,
  Mail
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { format, formatDistanceToNow } from 'date-fns';
import { SubBatchProgress } from '@/components/feed-imports/SubBatchProgress';

interface PhaseProgress {
  phase: 'enrichment' | 'embedding' | 'aeo_pipeline';
  phase_number: number;
  total_phases: number;
  processed: number;
  total: number;
  percentage: number;
  status_text: string;
  needs_action?: boolean;
  pipeline_run_id?: string;
  failed?: number;
}

interface FeedConfig {
  id: string;
  site_id: string;
  name: string;
  feed_url: string;
  feed_format: string;
  schedule_frequency: string;
  schedule_time: string | null;
  schedule_day: number | null;
  schedule_timezone: string;
  selected_categories: string[] | null;
  processing_options: {
    auto_normalize: boolean;
    auto_filter_attributes: boolean;
    ai_enrichment: boolean;
    ai_research: string;
    aeo_generation: boolean;
    force_aeo_regeneration?: boolean;
    force_ai_enrichment?: boolean;
    auto_approve_pilot: boolean;
    auto_approve_threshold: number;
    max_total_products: number | null;
    // Pipeline optimization options
    enable_validation?: boolean;        // Run 5 LLM validators (default: false)
    enable_quality_gate?: boolean;      // Skip products with missing critical attributes (default: true)
    auto_approve_all?: boolean;         // Auto-approve all generated content (default: false)
    auto_approve_content_threshold?: number | null;  // Score-based auto-approval threshold 1-10 (default: null)
    // AEO Filter - controls which products get AEO generation
    aeo_filter?: {
      enabled: boolean;
      field: string;                    // e.g., 'custom_labels.custom_label_4'
      operator: 'equals' | 'not_equals' | 'in' | 'exists';
      value?: string;                   // For equals/not_equals
      values?: string[];                // For 'in' operator
      retain_published?: boolean;       // Sticky inclusion: keep products in scope once they appear in publisher_manifest_records
    } | null;
    update_strategy?: {
      mode?: 'full_replace' | 'feed_only' | 'smart_merge';
      preserve_local_edits?: boolean;
      preserve_enrichments?: boolean;
      preserve_research?: boolean;
      field_policies?: Record<string, string>;
    };
  };
  attribute_config?: {
    excluded_attributes: string[];
  };
  status: 'draft' | 'active' | 'paused';
  sub_batch_size?: number;
  notification_emails?: string[];
  last_import_at: string | null;
  next_run_at: string | null;
  last_analysis: {
    total_products: number;
    total_categories: number;
    categories: { path: string; product_count: number }[];
  } | null;
  latest_run?: {
    id: string;
    status: string;
    started_at: string | null;
    phase_progress?: PhaseProgress;
  };
  created_at: string;
  updated_at: string;
}

interface FeedAnalysis {
  success: boolean;
  duration_ms: number;
  file_size_bytes?: number;
  format?: string;
  total_products?: number;
  total_categories?: number;
  categories?: { path: string; product_count: number; depth: number }[];
  attributes?: { name: string; populated_percentage: number }[];
  error?: string;
}

interface ImportRun {
  id: string;
  config_id: string;
  config_name?: string;
  // Sequential flow: pending → running → enriching → embedding → aeo_processing → completed
  status: 'pending' | 'running' | 'enriching' | 'embedding' | 'aeo_processing' | 'processing' | 'completed' | 'failed' | 'cancelled';
  started_at: string | null;
  completed_at: string | null;
  error_message?: string;
  pipeline_run_id?: string;
  enrichment_batch_id?: string;
  embedding_batch_id?: string;
  phase_progress?: PhaseProgress;
  processing_options?: {
    ai_enrichment?: boolean;
    aeo_generation?: boolean;
    force_aeo_regeneration?: boolean;
    force_ai_enrichment?: boolean;
    ai_research?: string;
    auto_normalize?: boolean;
  };
  summary?: {
    total_products: number;
    new_products: number;
    updated_products: number;
    duration_ms: number;
    total_duration_ms?: number;
    total_duration_formatted?: string;
    processing_time_ms?: number;
    processing_time_formatted?: string;
    has_async_jobs?: boolean;
    pilot_category?: string;
    aeo_generated?: number;
    aeo_validated?: number;
    pipeline_completed?: boolean;
  };
  steps: ImportStep[];
  // Zombie detection
  is_zombie?: boolean;
  zombie_reason?: string;
  zombie_minutes?: number;
}

interface ImportStep {
  id: string;
  step_name: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  started_at: string | null;
  completed_at: string | null;
  metrics?: Record<string, number | string | boolean>;
  error_message?: string;
}

interface CategoryReadiness {
  category: string;
  in_feed: number;
  in_catalog: number;
  new_in_feed: number;
  missing_from_feed: number;
  updated: number;
  enrichment_kept: number;
  enriched_catalog: number;
  aeo_eligible_feed: number;
  aeo_generated_feed: number;
  aeo_eligible_catalog: number;
  aeo_generated_catalog: number;
  aeo_eligible_with_generation: number;
  gaps_analyzed: boolean;
  gaps_applied: number;
  gaps_pending: number;
  gaps_needs_expert: number;
}

interface ImportPreview {
  preview: {
    total_products: number;
    new_products: number;
    updated_products: number;
    unchanged_products: number;
    changes_by_field: Record<string, { updated: number; preserved: number }>;
    local_modifications_preserved: number;
    enrichments_preserved: number;
    sample_changes: Array<{
      product_id: string;
      product_name: string;
      changes: Array<{
        field: string;
        old: unknown;
        new: unknown;
        action: 'update' | 'preserve';
        reason?: string;
        policy: string;
      }>;
    }>;
    // Feed misalignment detection
    unavailable_products: number;
    unavailable_products_sample: Array<{
      product_id: string;
      product_name: string;
    }>;
    needs_regeneration: number;
    regeneration_sample: Array<{
      product_id: string;
      product_name: string;
      changed_fields: string[];
    }>;
  };
  categories?: CategoryReadiness[];
  totals?: CategoryReadiness;
  aeo_filter_active?: boolean;
  strategy_applied: {
    mode: string;
    preserve_local_edits: boolean;
    preserve_enrichments: boolean;
    preserve_research: boolean;
  };
  duration_ms: number;
  generated_at: string;
}

interface WizardState {
  step: number;
  name: string;
  feed_url: string;
  feed_format: string;
  schedule_frequency: string;
  schedule_time: string;
  schedule_timezone: string;
  // File upload support
  sourceType: 'url' | 'upload';
  uploadedFile: { name: string; size: number; url: string } | null;
  uploading: boolean;
  processing_options: {
    auto_normalize: boolean;
    auto_filter_attributes: boolean;
    ai_enrichment: boolean;
    ai_research: string;
    aeo_generation: boolean;
    force_aeo_regeneration?: boolean;
    force_ai_enrichment?: boolean;
    auto_approve_pilot: boolean;
    auto_approve_threshold: number;
    max_total_products: number | null;
    // Pipeline optimization options
    enable_validation?: boolean;        // Run 5 LLM validators (default: false)
    enable_quality_gate?: boolean;      // Skip products with missing critical attributes (default: true)
    auto_approve_all?: boolean;         // Auto-approve all generated content (default: false)
    auto_approve_content_threshold?: number | null;  // Score-based auto-approval threshold 1-10 (default: null)
    // AEO Filter - controls which products get AEO generation
    aeo_filter?: {
      enabled: boolean;
      field: string;
      operator: 'equals' | 'not_equals' | 'in' | 'exists';
      value?: string;
      values?: string[];
      retain_published?: boolean;
    } | null;
    update_strategy?: {
      mode?: 'full_replace' | 'feed_only' | 'smart_merge';
      preserve_local_edits?: boolean;
      preserve_enrichments?: boolean;
      preserve_research?: boolean;
      field_policies?: Record<string, string>;
    };
  };
  analysis: FeedAnalysis | null;
  selectedCategories: string[];
  excludedAttributes: string[];
  testResult: { success: boolean; error?: string; format?: string } | null;
}

const initialWizardState: WizardState = {
  step: 1,
  name: '',
  feed_url: '',
  feed_format: 'auto',
  schedule_frequency: 'daily',
  schedule_time: '03:00',
  schedule_timezone: 'UTC',
  sourceType: 'url',
  uploadedFile: null,
  uploading: false,
  processing_options: {
    auto_normalize: true,
    auto_filter_attributes: true,
    ai_enrichment: true,
    ai_research: 'mark_for_later',
    aeo_generation: true,
    force_aeo_regeneration: false,
    force_ai_enrichment: false,
    auto_approve_pilot: false,
    auto_approve_threshold: 80,
    max_total_products: null,
  },
  analysis: null,
  selectedCategories: [],
  excludedAttributes: [],
  testResult: null,
};

export function ProductFeedImports() {
  const { selectedSite } = useSite();
  const [feedConfigs, setFeedConfigs] = useState<FeedConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [wizard, setWizard] = useState<WizardState>(initialWizardState);
  const [testing, setTesting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deletePreview, setDeletePreview] = useState<{
    config_id: string;
    config_name: string;
    import_runs: number;
    data_sources: number;
    products: number;
    kb_entries: number;
  } | null>(null);
  const [loadingDeletePreview, setLoadingDeletePreview] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentRun, setCurrentRun] = useState<ImportRun | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const [triggering, setTriggering] = useState<string | null>(null);
  // Ref to prevent double-click race condition (checked synchronously before API call)
  const triggeringRef = useRef<string | null>(null);
  const [historyConfigId, setHistoryConfigId] = useState<string | null>(null);
  const [runHistory, setRunHistory] = useState<ImportRun[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedHistoryRun, setSelectedHistoryRun] = useState<ImportRun | null>(null);
  const [editingConfig, setEditingConfig] = useState<FeedConfig | null>(null);
  const [editForm, setEditForm] = useState<Partial<FeedConfig>>({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [previewConfigId, setPreviewConfigId] = useState<string | null>(null);
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [editCategoryAnalysis, setEditCategoryAnalysis] = useState<{
    categories: { path: string; product_count: number; depth: number }[];
    loading: boolean;
    error: string | null;
  } | null>(null);

  useEffect(() => {
    if (selectedSite?.id) {
      loadFeedConfigs();
    }
  }, [selectedSite?.id]);

  // Auto-refresh when there's an active import (every 10 seconds)
  useEffect(() => {
    const hasActiveImport = feedConfigs.some(config =>
      config.latest_run?.status &&
      ['running', 'enriching', 'embedding', 'aeo_processing', 'processing', 'sub_batch_processing'].includes(config.latest_run.status)
    );

    if (!hasActiveImport) return;

    const interval = setInterval(() => {
      loadFeedConfigs();
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, [feedConfigs, selectedSite?.id]);

  const loadFeedConfigs = async () => {
    if (!selectedSite?.id) return;
    setLoading(true);
    try {
      const response = await authFetch(`/api/feed-imports?site_id=${selectedSite.id}`);
      if (response.ok) {
        const data = await response.json();
        setFeedConfigs(data);
      }
    } catch (error) {
      console.error('Error loading feed configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const testFeedUrl = async () => {
    if (!wizard.feed_url) return;
    setTesting(true);
    setWizard(prev => ({ ...prev, testResult: null }));
    try {
      const response = await authFetch('/api/feed-imports/test-download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: wizard.feed_url }),
      });
      const result = await response.json();
      setWizard(prev => ({
        ...prev,
        testResult: result,
        feed_format: result.format || 'auto',
      }));
    } catch (error) {
      setWizard(prev => ({
        ...prev,
        testResult: { success: false, error: 'Failed to test feed URL' },
      }));
    } finally {
      setTesting(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedSite) return;

    setWizard(prev => ({ ...prev, uploading: true, testResult: null }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('site_id', selectedSite.id);

      const response = await authFetch('/api/feed-imports/upload', {
        method: 'POST',
        body: formData,
        // Don't set Content-Type - browser will set it with boundary for multipart
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const result = await response.json();

      setWizard(prev => ({
        ...prev,
        uploading: false,
        feed_url: result.url,
        feed_format: result.format || 'auto',
        uploadedFile: {
          name: file.name,
          size: file.size,
          url: result.url,
        },
        testResult: {
          success: true,
          format: result.format,
        },
      }));
    } catch (error) {
      console.error('Upload error:', error);
      setWizard(prev => ({
        ...prev,
        uploading: false,
        testResult: {
          success: false,
          error: error instanceof Error ? error.message : 'Upload failed',
        },
      }));
    }

    // Reset file input
    event.target.value = '';
  };

  const analyzeFeed = async () => {
    if (!wizard.feed_url) return;
    setAnalyzing(true);
    try {
      const response = await authFetch('/api/feed-imports/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: wizard.feed_url }),
      });
      const result = await response.json();
      setWizard(prev => ({
        ...prev,
        analysis: result,
        // Don't auto-advance - let user select categories first
      }));
    } catch (error) {
      console.error('Error analyzing feed:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const toggleCategorySelection = (categoryPath: string) => {
    setWizard(prev => {
      const isSelected = prev.selectedCategories.includes(categoryPath);
      return {
        ...prev,
        selectedCategories: isSelected
          ? prev.selectedCategories.filter(c => c !== categoryPath)
          : [...prev.selectedCategories, categoryPath],
      };
    });
  };

  const selectAllCategories = () => {
    if (!wizard.analysis?.categories) return;
    setWizard(prev => ({
      ...prev,
      selectedCategories: wizard.analysis!.categories!.map(c => c.path),
    }));
  };

  const deselectAllCategories = () => {
    setWizard(prev => ({
      ...prev,
      selectedCategories: [],
    }));
  };

  const saveFeedConfig = async (status: 'draft' | 'paused' = 'paused') => {
    if (!selectedSite?.id) return;
    setSaving(true);
    try {
      // Clean up processing options before saving
      const cleanedProcessingOptions = { ...wizard.processing_options };
      if (cleanedProcessingOptions.aeo_filter?.values) {
        cleanedProcessingOptions.aeo_filter = {
          ...cleanedProcessingOptions.aeo_filter,
          values: cleanedProcessingOptions.aeo_filter.values.filter(Boolean)
        };
      }
      const response = await authFetch('/api/feed-imports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: selectedSite.id,
          name: wizard.name,
          feed_url: wizard.feed_url,
          feed_format: wizard.feed_format,
          schedule_frequency: wizard.schedule_frequency,
          schedule_time: wizard.schedule_time,
          schedule_timezone: wizard.schedule_timezone,
          selected_categories: wizard.selectedCategories.length > 0 ? wizard.selectedCategories : null,
          processing_options: cleanedProcessingOptions,
          attribute_config: wizard.excludedAttributes.length > 0
            ? { excluded_attributes: wizard.excludedAttributes }
            : null,
          status,
        }),
      });
      if (response.ok) {
        setShowWizard(false);
        setWizard(initialWizardState);
        loadFeedConfigs();
      }
    } catch (error) {
      console.error('Error saving feed config:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleFeedStatus = async (config: FeedConfig) => {
    const newStatus = config.status === 'active' ? 'paused' : 'active';
    try {
      const endpoint = newStatus === 'paused'
        ? `/api/feed-imports/${config.id}/pause`
        : `/api/feed-imports/${config.id}/resume`;
      const response = await authFetch(endpoint, { method: 'POST' });
      if (response.ok) {
        loadFeedConfigs();
      }
    } catch (error) {
      console.error('Error toggling feed status:', error);
    }
  };

  const loadDeletePreview = async (configId: string) => {
    setDeleteConfirm(configId);
    setLoadingDeletePreview(true);
    setDeletePreview(null);
    try {
      const response = await authFetch(`/api/feed-imports/${configId}/delete-preview`);
      if (response.ok) {
        const preview = await response.json();
        setDeletePreview(preview);
      }
    } catch (error) {
      console.error('Error loading delete preview:', error);
    } finally {
      setLoadingDeletePreview(false);
    }
  };

  const deleteFeedConfig = async (configId: string, deleteData: boolean) => {
    setDeleting(true);
    try {
      const response = await authFetch(`/api/feed-imports/${configId}?deleteData=${deleteData}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        setDeleteConfirm(null);
        setDeletePreview(null);
        loadFeedConfigs();
      }
    } catch (error) {
      console.error('Error deleting feed config:', error);
    } finally {
      setDeleting(false);
    }
  };

  const triggerImport = async (configId: string) => {
    // Synchronous check to prevent double-click race condition
    // React state updates are async, so we use a ref for immediate check
    if (triggeringRef.current !== null) {
      console.log('Import already in progress, ignoring duplicate click');
      return;
    }
    triggeringRef.current = configId;
    setTriggering(configId);
    try {
      const response = await authFetch(`/api/feed-imports/${configId}/trigger`, {
        method: 'POST',
      });
      if (response.ok) {
        const run = await response.json();
        setCurrentRun(run);
        setShowProgress(true);
        // Refresh the list to update latest_run status
        // Note: triggering will be cleared by the useEffect below once feedConfigs updates
        await loadFeedConfigs();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to trigger import');
        triggeringRef.current = null;
        setTriggering(null);
      }
    } catch (error) {
      console.error('Error triggering import:', error);
      alert('Failed to trigger import');
      triggeringRef.current = null;
      setTriggering(null);
    }
  };

  // Clear triggering state once feedConfigs updates and shows an active run
  // This prevents race conditions where button re-enables before state updates
  useEffect(() => {
    if (triggering) {
      const config = feedConfigs.find(c => c.id === triggering);
      const hasActiveRun = config?.latest_run?.status &&
        ['pending', 'running', 'enriching', 'embedding', 'aeo_processing', 'processing', 'sub_batch_processing', 'enrichment_review'].includes(config.latest_run.status);
      if (hasActiveRun) {
        // Config now shows active run, safe to clear triggering
        // Button will stay disabled due to latest_run.status check
        triggeringRef.current = null;
        setTriggering(null);
      } else {
        // Safety timeout: clear triggering after 10s if run status never updates
        const timeout = setTimeout(() => {
          triggeringRef.current = null;
          setTriggering(null);
        }, 10000);
        return () => clearTimeout(timeout);
      }
    }
  }, [feedConfigs, triggering]);

  const pollRunStatus = async (runId: string) => {
    try {
      const response = await authFetch(`/api/feed-imports/runs/${runId}`);
      if (response.ok) {
        const run = await response.json();
        setCurrentRun(run);
        return run;
      }
    } catch (error) {
      console.error('Error polling run status:', error);
    }
    return null;
  };

  const cancelImport = async (runId: string) => {
    try {
      const response = await authFetch(`/api/feed-imports/runs/${runId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Cancelled by user' }),
      });
      if (response.ok) {
        const run = await response.json();
        setCurrentRun(run);
      }
    } catch (error) {
      console.error('Error cancelling import:', error);
    }
  };

  const forceCancelImport = async (runId: string, configId?: string) => {
    if (!confirm('Are you sure you want to force cancel this import? This will cancel all associated pipeline runs and batch jobs.')) {
      return;
    }
    try {
      const response = await authFetch(`/api/feed-imports/runs/${runId}/force-cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Force cancelled by user' }),
      });
      if (response.ok) {
        const run = await response.json();
        setCurrentRun(run);
        setSelectedHistoryRun(run);
        // Refresh the run history if we have a configId
        if (configId) {
          loadRunHistory(configId);
        }
        // Refresh configs to update latest_run status
        loadConfigs();
      }
    } catch (error) {
      console.error('Error force cancelling import:', error);
    }
  };

  const loadRunHistory = async (configId: string) => {
    setHistoryConfigId(configId);
    setLoadingHistory(true);
    setRunHistory([]);
    try {
      const response = await authFetch(`/api/feed-imports/${configId}/runs?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setRunHistory(data.runs || data);
      }
    } catch (error) {
      console.error('Error loading run history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const viewRunDetails = async (runId: string) => {
    try {
      const response = await authFetch(`/api/feed-imports/runs/${runId}`);
      if (response.ok) {
        const run = await response.json();
        setSelectedHistoryRun(run);
      }
    } catch (error) {
      console.error('Error loading run details:', error);
    }
  };

  const openEditDialog = (config: FeedConfig) => {
    setEditingConfig(config);
    setEditForm({
      name: config.name,
      feed_url: config.feed_url,
      feed_format: config.feed_format,
      schedule_frequency: config.schedule_frequency,
      schedule_time: config.schedule_time || '',
      schedule_timezone: config.schedule_timezone,
      processing_options: { ...config.processing_options },
      attribute_config: config.attribute_config || { excluded_attributes: [] },
      notification_emails: config.notification_emails || [],
      sub_batch_size: config.sub_batch_size || 50,
      selected_categories: config.selected_categories || [],
    });
    setEditCategoryAnalysis(null);
  };

  const saveConfigEdits = async () => {
    if (!editingConfig) return;
    setSavingEdit(true);
    try {
      // Clean up form data before saving
      const cleanedForm = { ...editForm };
      if (cleanedForm.processing_options?.aeo_filter?.values) {
        cleanedForm.processing_options = {
          ...cleanedForm.processing_options,
          aeo_filter: {
            ...cleanedForm.processing_options.aeo_filter,
            values: cleanedForm.processing_options.aeo_filter.values.filter(Boolean)
          }
        };
      }
      const response = await authFetch(`/api/feed-imports/${editingConfig.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanedForm),
      });
      if (response.ok) {
        setEditingConfig(null);
        setEditForm({});
        loadFeedConfigs();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to save changes');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const analyzeEditFeed = async () => {
    if (!editingConfig) return;
    setEditCategoryAnalysis({ categories: [], loading: true, error: null });
    try {
      const response = await authFetch(`/api/feed-imports/${editingConfig.id}/analyze`, {
        method: 'POST',
      });
      const result = await response.json();
      if (response.ok) {
        setEditCategoryAnalysis({
          categories: result.categories || [],
          loading: false,
          error: null
        });
      } else {
        setEditCategoryAnalysis({
          categories: [],
          loading: false,
          error: result.error || 'Failed to analyze feed'
        });
      }
    } catch (error) {
      setEditCategoryAnalysis({
        categories: [],
        loading: false,
        error: 'Failed to analyze feed'
      });
    }
  };

  const loadImportPreview = async (configId: string) => {
    setPreviewConfigId(configId);
    setLoadingPreview(true);
    setImportPreview(null);
    try {
      const response = await authFetch(`/api/feed-imports/${configId}/preview`, {
        method: 'POST',
      });
      if (response.ok) {
        const preview = await response.json();
        setImportPreview(preview);
      } else {
        const error = await response.json();
        console.error('Error loading preview:', error);
        alert(error.error || 'Failed to load preview');
        setPreviewConfigId(null);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      alert('Failed to load preview');
      setPreviewConfigId(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Poll for run status updates
  useEffect(() => {
    // Sequential flow statuses that indicate async work is happening
    const activeStatuses = ['pending', 'running', 'enriching', 'embedding', 'aeo_processing', 'processing', 'sub_batch_processing'];
    if (!currentRun || !activeStatuses.includes(currentRun.status)) {
      return;
    }

    // Poll faster for running (sync steps), slower for async phases (can take hours)
    const asyncStatuses = ['enriching', 'embedding', 'aeo_processing', 'processing', 'sub_batch_processing'];
    const pollInterval = asyncStatuses.includes(currentRun.status) ? 30000 : 2000;

    const interval = setInterval(async () => {
      const run = await pollRunStatus(currentRun.id);
      if (run && !activeStatuses.includes(run.status)) {
        clearInterval(interval);
        loadFeedConfigs(); // Refresh the list when done
      }
    }, pollInterval);

    return () => clearInterval(interval);
  }, [currentRun?.id, currentRun?.status]);

  // Poll for selected history run updates (when viewing details of an active run)
  useEffect(() => {
    const activeStatuses = ['pending', 'running', 'enriching', 'embedding', 'aeo_processing', 'processing', 'sub_batch_processing'];
    if (!selectedHistoryRun || !activeStatuses.includes(selectedHistoryRun.status)) {
      return;
    }

    // Poll every 5 seconds for active runs being viewed
    const interval = setInterval(async () => {
      try {
        const response = await authFetch(`/api/feed-imports/runs/${selectedHistoryRun.id}`);
        if (response.ok) {
          const run = await response.json();
          setSelectedHistoryRun(run);
          // Also refresh the history list to show updated status
          if (!activeStatuses.includes(run.status)) {
            clearInterval(interval);
            fetchRunHistory(selectedHistoryRun.config_id);
          }
        }
      } catch (error) {
        console.error('Error polling run status:', error);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedHistoryRun?.id, selectedHistoryRun?.status]);

  const getStepIcon = (step: ImportStep) => {
    switch (step.status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'running':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'skipped':
        return <Clock className="h-5 w-5 text-gray-400" />;
      default:
        return <Clock className="h-5 w-5 text-gray-300" />;
    }
  };

  const getStepLabel = (stepName: string) => {
    const labels: Record<string, string> = {
      download: 'Download Feed',
      parse: 'Parse Feed',
      uniqueness: 'Check Uniqueness',
      normalization: 'Store Products',
      enrichment: 'AI Enrichment',
      enrichment_gate: 'Quality Gate',
      research: 'AI Research',
      aeo: 'AEO Generation',
    };
    return labels[stepName] || stepName;
  };

  // Get the actual processing duration for a step (not just queue time)
  const getStepDuration = (step: ImportStep): number | null => {
    if (!step.metrics) return null;
    // For async steps, prefer actual_duration_ms or pipeline_duration_ms over initial queue time
    if (step.step_name === 'enrichment' && step.metrics.actual_duration_ms) {
      return step.metrics.actual_duration_ms as number;
    }
    if (step.step_name === 'aeo' && step.metrics.pipeline_duration_ms) {
      return step.metrics.pipeline_duration_ms as number;
    }
    return step.metrics.duration_ms as number || null;
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }
    return `${minutes}m ${seconds}s`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500">Active</Badge>;
      case 'paused':
        return <Badge variant="secondary">Paused</Badge>;
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatSchedule = (config: FeedConfig) => {
    if (config.schedule_frequency === 'manual') return 'Manual';
    if (config.schedule_frequency === 'hourly') return 'Every hour';
    if (config.schedule_frequency === 'daily') {
      return `Daily at ${config.schedule_time || '00:00'} ${config.schedule_timezone}`;
    }
    if (config.schedule_frequency === 'weekly') {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return `Weekly on ${days[config.schedule_day || 0]} at ${config.schedule_time}`;
    }
    return config.schedule_frequency;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">Feed Imports</h1>
          <p className="text-muted-foreground">
            Configure and manage automatic product feed imports for enrichment and AEO generation.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadFeedConfigs} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowWizard(true)} disabled={!selectedSite}>
            <Plus className="h-4 w-4 mr-2" />
            Add Feed Import
          </Button>
        </div>
      </div>

      {!selectedSite && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Please select a site from the header to manage feed imports.
          </AlertDescription>
        </Alert>
      )}

      {selectedSite && loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {selectedSite && !loading && feedConfigs.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Upload className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Feed Imports Configured</h3>
            <p className="text-muted-foreground text-center mb-4">
              Set up automatic product feed imports to keep your catalog enriched and up-to-date.
            </p>
            <Button onClick={() => setShowWizard(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Configure First Feed
            </Button>
          </CardContent>
        </Card>
      )}

      {selectedSite && !loading && feedConfigs.length > 0 && (
        <div className="space-y-4">
          {feedConfigs.map((config) => (
            <Card key={config.id} className="hover:border-primary/50 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div
                    className="space-y-2 cursor-pointer flex-1"
                    onClick={() => loadRunHistory(config.id)}
                  >
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold hover:text-primary">{config.name}</h3>
                      {getStatusBadge(config.status)}
                      <span className="text-xs text-muted-foreground">Click for details</span>
                    </div>
                    <p className="text-sm text-muted-foreground font-mono truncate max-w-xl">
                      {config.feed_url}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatSchedule(config)}
                      </span>
                      {config.last_analysis && (
                        <>
                          <span className="flex items-center gap-1">
                            <Database className="h-4 w-4" />
                            {config.last_analysis.total_products.toLocaleString()} products
                          </span>
                          <span className="flex items-center gap-1">
                            <FolderTree className="h-4 w-4" />
                            {config.last_analysis.total_categories} categories
                          </span>
                        </>
                      )}
                    </div>
                    {config.last_import_at && !config.latest_run?.phase_progress && (
                      <p className="text-xs text-muted-foreground">
                        Last import: {formatDistanceToNow(new Date(config.last_import_at), { addSuffix: true })}
                      </p>
                    )}
                    {/* Active Import Progress Indicator */}
                    {config.latest_run?.phase_progress && (
                      <div className={`mt-2 p-2 rounded-md border ${
                        config.latest_run.phase_progress.needs_action
                          ? 'bg-amber-50 dark:bg-amber-950 border-amber-300 dark:border-amber-700'
                          : 'bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800'
                      }`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            {config.latest_run.phase_progress.needs_action ? (
                              <AlertTriangle className="h-4 w-4 text-amber-600" />
                            ) : (
                              <Loader2 className="h-4 w-4 text-orange-600 animate-spin" />
                            )}
                            <span className={`text-sm font-medium ${
                              config.latest_run.phase_progress.needs_action
                                ? 'text-amber-800 dark:text-amber-200'
                                : 'text-orange-800 dark:text-orange-200'
                            }`}>
                              {config.latest_run.phase_progress.status_text}
                            </span>
                          </div>
                          {config.latest_run.phase_progress.pipeline_run_id && (
                            <a
                              href="/pipeline"
                              className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                            >
                              {config.latest_run.phase_progress.needs_action ? 'Review →' : 'View →'}
                            </a>
                          )}
                        </div>
                        {config.latest_run.phase_progress.total > 0 && (
                          <Progress value={config.latest_run.phase_progress.percentage} className="h-1.5 mt-1" />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => triggerImport(config.id)}
                      disabled={triggering === config.id || (config.latest_run?.status && ['pending', 'running', 'enriching', 'embedding', 'aeo_processing', 'processing', 'sub_batch_processing', 'enrichment_review'].includes(config.latest_run.status))}
                    >
                      {triggering === config.id ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Starting...
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-1" />
                          Run Now
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleFeedStatus(config)}
                    >
                      {config.status === 'active' ? (
                        <>
                          <Pause className="h-4 w-4 mr-1" />
                          Pause
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Activate
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); openEditDialog(config); }}
                      title="Edit configuration"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); loadRunHistory(config.id); }}
                      title="View run history"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); loadImportPreview(config.id); }}
                      title="Preview import changes"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); loadDeletePreview(config.id); }}
                      title="Delete feed import"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Processing Options Summary */}
                <div className="mt-4 pt-4 border-t flex items-center gap-4 text-xs">
                  <span className="text-muted-foreground">Processing:</span>
                  {config.processing_options.auto_normalize && (
                    <Badge variant="outline" className="text-xs">Normalize</Badge>
                  )}
                  {config.processing_options.ai_enrichment && (
                    <Badge variant="outline" className="text-xs">
                      <Sparkles className="h-3 w-3 mr-1" />
                      AI Enrichment
                    </Badge>
                  )}
                  {config.processing_options.aeo_generation && (
                    <Badge variant="outline" className="text-xs">AEO Generation</Badge>
                  )}
                  {config.processing_options.force_aeo_regeneration && (
                    <Badge variant="warning" className="text-xs">Force AEO Regen</Badge>
                  )}
                  {config.processing_options.force_ai_enrichment && (
                    <Badge variant="warning" className="text-xs">Force Re-Enrich</Badge>
                  )}
                  {config.processing_options.auto_approve_pilot && (
                    <Badge variant="outline" className="text-xs text-green-600">
                      Auto-approve {config.processing_options.auto_approve_threshold || 80}%
                    </Badge>
                  )}
                  {config.processing_options.max_total_products && (
                    <Badge variant="outline" className="text-xs text-blue-600">
                      Max {config.processing_options.max_total_products} products
                    </Badge>
                  )}
                  {config.processing_options.aeo_filter?.enabled && (
                    <Badge variant="outline" className="text-xs text-amber-600">
                      AEO Filter: {config.processing_options.aeo_filter.field.split('.').pop()}
                    </Badge>
                  )}
                  {config.processing_options.update_strategy?.mode && config.processing_options.update_strategy.mode !== 'smart_merge' && (
                    <Badge variant="outline" className="text-xs text-purple-600">
                      {config.processing_options.update_strategy.mode === 'full_replace' ? 'Full Replace' : 'Feed Only'}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Feed Wizard Dialog */}
      <Dialog open={showWizard} onOpenChange={(open) => {
        if (!open) {
          setShowWizard(false);
          setWizard(initialWizardState);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Feed Import</DialogTitle>
            <DialogDescription>
              Configure a new product feed import for automatic processing.
            </DialogDescription>
          </DialogHeader>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-6">
            {[1, 2, 3, 4].map((step) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      wizard.step >= step
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {wizard.step > step ? <CheckCircle className="h-4 w-4" /> : step}
                  </div>
                  <span className="text-xs mt-1 text-muted-foreground">
                    {step === 1 && 'Source'}
                    {step === 2 && 'Analyze'}
                    {step === 3 && 'Options'}
                    {step === 4 && 'Schedule'}
                  </span>
                </div>
                {step < 4 && (
                  <div className={`flex-1 h-0.5 mx-2 ${wizard.step > step ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Feed Source */}
          {wizard.step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="feed-name">Feed Name</Label>
                <Input
                  id="feed-name"
                  placeholder="e.g., Main Product Feed"
                  value={wizard.name}
                  onChange={(e) => setWizard(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Feed Source</Label>
                <Tabs
                  value={wizard.sourceType}
                  onValueChange={(value) => setWizard(prev => ({
                    ...prev,
                    sourceType: value as 'url' | 'upload',
                    testResult: null,
                    feed_url: value === 'url' ? prev.feed_url : (prev.uploadedFile?.url || ''),
                  }))}
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="url">
                      <SearchIcon className="h-4 w-4 mr-2" />
                      URL
                    </TabsTrigger>
                    <TabsTrigger value="upload">
                      <Upload className="h-4 w-4 mr-2" />
                      Upload File
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="url" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="feed-url">Feed URL</Label>
                      <Input
                        id="feed-url"
                        placeholder="https://example.com/products.tsv"
                        value={wizard.feed_url}
                        onChange={(e) => setWizard(prev => ({ ...prev, feed_url: e.target.value, testResult: null }))}
                      />
                    </div>
                    <Button
                      onClick={testFeedUrl}
                      disabled={!wizard.feed_url || testing}
                      variant="outline"
                    >
                      {testing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <SearchIcon className="h-4 w-4 mr-2" />
                      )}
                      Test Connection
                    </Button>
                  </TabsContent>

                  <TabsContent value="upload" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Upload Feed File</Label>
                      <p className="text-sm text-muted-foreground">
                        Upload a CSV, TSV, or XML file (max 100MB)
                      </p>
                    </div>

                    {wizard.uploadedFile ? (
                      <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/50">
                        <FileText className="h-8 w-8 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium">{wizard.uploadedFile.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {(wizard.uploadedFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setWizard(prev => ({
                            ...prev,
                            uploadedFile: null,
                            feed_url: '',
                            testResult: null,
                          }))}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <div className="relative">
                        <input
                          type="file"
                          accept=".csv,.tsv,.xml,.txt"
                          onChange={handleFileUpload}
                          disabled={wizard.uploading}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                        />
                        <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors">
                          {wizard.uploading ? (
                            <>
                              <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin text-primary" />
                              <p className="text-sm text-muted-foreground">Uploading...</p>
                            </>
                          ) : (
                            <>
                              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                              <p className="text-sm font-medium">Click to upload or drag and drop</p>
                              <p className="text-xs text-muted-foreground mt-1">CSV, TSV, or XML files</p>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>

              {wizard.testResult && (
                <Alert variant={wizard.testResult.success ? 'default' : 'destructive'}>
                  {wizard.testResult.success ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  <AlertDescription>
                    {wizard.testResult.success
                      ? `${wizard.sourceType === 'upload' ? 'File uploaded' : 'Feed accessible'}! Detected format: ${wizard.testResult.format?.toUpperCase()}`
                      : wizard.testResult.error}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Step 2: Analyze Feed */}
          {wizard.step === 2 && (
            <div className="space-y-4">
              {!wizard.analysis && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">
                    Analyze the feed to discover products and categories.
                  </p>
                  <Button onClick={analyzeFeed} disabled={analyzing}>
                    {analyzing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Analyzing Feed...
                      </>
                    ) : (
                      <>
                        <SearchIcon className="h-4 w-4 mr-2" />
                        Analyze Feed
                      </>
                    )}
                  </Button>
                </div>
              )}

              {wizard.analysis && !wizard.analysis.success && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>{wizard.analysis.error}</AlertDescription>
                </Alert>
              )}

              {wizard.analysis && wizard.analysis.success && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold">{wizard.analysis.total_products?.toLocaleString()}</div>
                        <div className="text-sm text-muted-foreground">Products</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold">{wizard.analysis.total_categories}</div>
                        <div className="text-sm text-muted-foreground">Categories</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold">{wizard.analysis.format?.toUpperCase()}</div>
                        <div className="text-sm text-muted-foreground">Format</div>
                      </CardContent>
                    </Card>
                  </div>

                  {wizard.analysis.categories && wizard.analysis.categories.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Select Categories to Import</Label>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (wizard.analysis?.categories) {
                                setWizard(prev => ({
                                  ...prev,
                                  selectedCategories: wizard.analysis!.categories!.map(c => c.path),
                                }));
                              }
                            }}
                          >
                            Select All
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setWizard(prev => ({ ...prev, selectedCategories: [] }))}
                          >
                            Deselect All
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {wizard.selectedCategories.length} of {wizard.analysis.categories.length} categories selected
                      </p>
                      <ScrollArea className="h-64 border rounded-md p-2">
                        {wizard.analysis.categories.map((cat, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-3 py-2 hover:bg-muted/50 rounded px-2 cursor-pointer"
                            onClick={() => {
                              const isSelected = wizard.selectedCategories.includes(cat.path);
                              setWizard(prev => ({
                                ...prev,
                                selectedCategories: isSelected
                                  ? prev.selectedCategories.filter(c => c !== cat.path)
                                  : [...prev.selectedCategories, cat.path],
                              }));
                            }}
                          >
                            <Checkbox
                              checked={wizard.selectedCategories.includes(cat.path)}
                              onCheckedChange={(checked) => {
                                const isSelected = wizard.selectedCategories.includes(cat.path);
                                setWizard(prev => ({
                                  ...prev,
                                  selectedCategories: checked && !isSelected
                                    ? [...prev.selectedCategories, cat.path]
                                    : prev.selectedCategories.filter(c => c !== cat.path),
                                }));
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="flex-1 truncate text-sm">{cat.path}</span>
                            <Badge variant="secondary">{cat.product_count}</Badge>
                          </div>
                        ))}
                      </ScrollArea>
                    </div>
                  )}

                  {/* Attribute Exclusion */}
                  {wizard.analysis.attributes && wizard.analysis.attributes.length > 0 && (
                    <div className="space-y-2 mt-6 pt-6 border-t">
                      <div className="flex items-center justify-between">
                        <Label>Exclude Attributes (optional)</Label>
                        <span className="text-sm text-muted-foreground">
                          {wizard.excludedAttributes.length} excluded
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Select attributes to exclude from import (e.g., package weight, internal codes)
                      </p>
                      <ScrollArea className="h-48 border rounded-md p-2">
                        {[...wizard.analysis.attributes].sort((a, b) => a.name.localeCompare(b.name)).map((attr, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-3 py-2 hover:bg-muted/50 rounded px-2 cursor-pointer"
                            onClick={() => {
                              const isExcluded = wizard.excludedAttributes.includes(attr.name);
                              setWizard(prev => ({
                                ...prev,
                                excludedAttributes: isExcluded
                                  ? prev.excludedAttributes.filter(a => a !== attr.name)
                                  : [...prev.excludedAttributes, attr.name],
                              }));
                            }}
                          >
                            <Checkbox
                              checked={wizard.excludedAttributes.includes(attr.name)}
                              onCheckedChange={(checked) => {
                                setWizard(prev => ({
                                  ...prev,
                                  excludedAttributes: checked
                                    ? [...prev.excludedAttributes, attr.name]
                                    : prev.excludedAttributes.filter(a => a !== attr.name),
                                }));
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="flex-1 text-sm font-mono">{attr.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {attr.populated_percentage}% populated
                            </Badge>
                          </div>
                        ))}
                      </ScrollArea>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Processing Options */}
          {wizard.step === 3 && (
            <div className="space-y-6">
              <div className="space-y-4">
                <h4 className="font-medium">Processing Options</h4>

                {/* Quick Presets */}
                <div className="flex gap-2 pb-2 border-b">
                  <Button
                    variant={wizard.processing_options.aeo_generation ? "default" : "outline"}
                    size="sm"
                    onClick={() => setWizard(prev => ({
                      ...prev,
                      processing_options: {
                        ...prev.processing_options,
                        auto_normalize: true,
                        ai_enrichment: true,
                        aeo_generation: true
                      }
                    }))}
                  >
                    Full Processing
                  </Button>
                  <Button
                    variant={!wizard.processing_options.aeo_generation && wizard.processing_options.ai_enrichment ? "default" : "outline"}
                    size="sm"
                    onClick={() => setWizard(prev => ({
                      ...prev,
                      processing_options: {
                        ...prev.processing_options,
                        auto_normalize: true,
                        ai_enrichment: true,
                        aeo_generation: false
                      }
                    }))}
                  >
                    Catalog Only
                  </Button>
                </div>
                {!wizard.processing_options.aeo_generation && (
                  <Alert className="bg-blue-50 dark:bg-blue-950/30 border-blue-200">
                    <Database className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Catalog Only Mode:</strong> Products will be imported and enriched, but no AEO content will be generated.
                      Use this to build your product catalog for category research before running content generation.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Auto-Normalize Attributes</Label>
                    <p className="text-sm text-muted-foreground">Standardize attribute names and values</p>
                  </div>
                  <Switch
                    checked={wizard.processing_options.auto_normalize}
                    onCheckedChange={(checked) => setWizard(prev => ({
                      ...prev,
                      processing_options: { ...prev.processing_options, auto_normalize: checked }
                    }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Auto-Filter Attributes</Label>
                    <p className="text-sm text-muted-foreground">Apply attribute evaluation rules</p>
                  </div>
                  <Switch
                    checked={wizard.processing_options.auto_filter_attributes}
                    onCheckedChange={(checked) => setWizard(prev => ({
                      ...prev,
                      processing_options: { ...prev.processing_options, auto_filter_attributes: checked }
                    }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>AI Enrichment</Label>
                    <p className="text-sm text-muted-foreground">Enrich products with AI-generated attributes</p>
                  </div>
                  <Switch
                    checked={wizard.processing_options.ai_enrichment}
                    onCheckedChange={(checked) => setWizard(prev => ({
                      ...prev,
                      processing_options: { ...prev.processing_options, ai_enrichment: checked }
                    }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>AEO Generation</Label>
                    <p className="text-sm text-muted-foreground">Generate AEO content for products</p>
                  </div>
                  <Switch
                    checked={wizard.processing_options.aeo_generation}
                    onCheckedChange={(checked) => setWizard(prev => ({
                      ...prev,
                      processing_options: { ...prev.processing_options, aeo_generation: checked }
                    }))}
                  />
                </div>

                {/* Auto-approve pilot settings - only shown when AEO generation is enabled */}
                {wizard.processing_options.aeo_generation && (
                  <div className="ml-4 pl-4 border-l-2 border-muted space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Auto-approve Pilot</Label>
                        <p className="text-sm text-muted-foreground">Automatically proceed to full generation if pilot success rate meets threshold</p>
                      </div>
                      <Switch
                        checked={wizard.processing_options.auto_approve_pilot}
                        onCheckedChange={(checked) => setWizard(prev => ({
                          ...prev,
                          processing_options: { ...prev.processing_options, auto_approve_pilot: checked }
                        }))}
                      />
                    </div>

                    {wizard.processing_options.auto_approve_pilot && (
                      <div className="space-y-2">
                        <Label>Success Threshold (%)</Label>
                        <p className="text-sm text-muted-foreground">Minimum pilot success rate to auto-approve</p>
                        <Input
                          type="number"
                          min={50}
                          max={100}
                          value={wizard.processing_options.auto_approve_threshold}
                          onChange={(e) => setWizard(prev => ({
                            ...prev,
                            processing_options: { ...prev.processing_options, auto_approve_threshold: parseInt(e.target.value) || 80 }
                          }))}
                          className="w-24"
                        />
                      </div>
                    )}

                    {/* AEO Product Filter */}
                    <Separator className="my-3" />
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Filter Products for AEO</Label>
                        <p className="text-sm text-muted-foreground">Only generate AEO for products matching filter criteria</p>
                      </div>
                      <Switch
                        checked={wizard.processing_options.aeo_filter?.enabled ?? false}
                        onCheckedChange={(checked) => setWizard(prev => ({
                          ...prev,
                          processing_options: {
                            ...prev.processing_options,
                            aeo_filter: {
                              ...(prev.processing_options.aeo_filter ?? { field: 'custom_labels.custom_label_4', operator: 'equals' as const, value: '' }),
                              enabled: checked,
                            },
                          }
                        }))}
                      />
                    </div>

                    {wizard.processing_options.aeo_filter?.enabled && (
                      <div className="space-y-3 mt-3 p-3 bg-muted/30 rounded-md">
                        <div className="space-y-2">
                          <Label>Field</Label>
                          <Select
                            value={wizard.processing_options.aeo_filter.field}
                            onValueChange={(value) => setWizard(prev => ({
                              ...prev,
                              processing_options: {
                                ...prev.processing_options,
                                aeo_filter: { ...prev.processing_options.aeo_filter!, field: value }
                              }
                            }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="custom_labels.custom_label_0">Custom Label 0</SelectItem>
                              <SelectItem value="custom_labels.custom_label_1">Custom Label 1</SelectItem>
                              <SelectItem value="custom_labels.custom_label_2">Custom Label 2</SelectItem>
                              <SelectItem value="custom_labels.custom_label_3">Custom Label 3</SelectItem>
                              <SelectItem value="custom_labels.custom_label_4">Custom Label 4</SelectItem>
                              <SelectItem value="brand">Brand</SelectItem>
                              <SelectItem value="category">Category</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Operator</Label>
                          <Select
                            value={wizard.processing_options.aeo_filter.operator}
                            onValueChange={(value: 'equals' | 'not_equals' | 'in' | 'exists') => setWizard(prev => ({
                              ...prev,
                              processing_options: {
                                ...prev.processing_options,
                                aeo_filter: { ...prev.processing_options.aeo_filter!, operator: value }
                              }
                            }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="equals">Equals</SelectItem>
                              <SelectItem value="not_equals">Not Equals</SelectItem>
                              <SelectItem value="in">In List</SelectItem>
                              <SelectItem value="exists">Exists (not empty)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {wizard.processing_options.aeo_filter.operator !== 'exists' && (
                          <div className="space-y-2">
                            <Label>{wizard.processing_options.aeo_filter.operator === 'in' ? 'Values (comma-separated)' : 'Value'}</Label>
                            <Input
                              placeholder={wizard.processing_options.aeo_filter.operator === 'in' ? '2, 3, 4' : '2'}
                              value={wizard.processing_options.aeo_filter.operator === 'in'
                                ? (wizard.processing_options.aeo_filter.values || []).join(', ')
                                : wizard.processing_options.aeo_filter.value || ''
                              }
                              onChange={(e) => setWizard(prev => {
                                const aeoFilter = prev.processing_options.aeo_filter!;
                                if (aeoFilter.operator === 'in') {
                                  // Don't filter empty values while typing - allows comma to be typed
                                  return {
                                    ...prev,
                                    processing_options: {
                                      ...prev.processing_options,
                                      aeo_filter: { ...aeoFilter, values: e.target.value.split(',').map(v => v.trim()) }
                                    }
                                  };
                                }
                                return {
                                  ...prev,
                                  processing_options: {
                                    ...prev.processing_options,
                                    aeo_filter: { ...aeoFilter, value: e.target.value }
                                  }
                                };
                              })}
                            />
                            <p className="text-xs text-muted-foreground">
                              {wizard.processing_options.aeo_filter.field.includes('custom_label') &&
                                'TradeInn: "2" = Super Top products'}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Retain Published — peer to the filter master toggle, not gated by it */}
                    <Separator className="my-3" />
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Retain published products</Label>
                        <p className="text-sm text-muted-foreground">
                          Keep products eligible for AEO once they appear in the publisher manifest, even if they no longer match the filter values. Prevents drop-off when categorization changes upstream.
                        </p>
                      </div>
                      <Switch
                        checked={wizard.processing_options.aeo_filter?.retain_published ?? false}
                        onCheckedChange={(checked) => setWizard(prev => ({
                          ...prev,
                          processing_options: {
                            ...prev.processing_options,
                            aeo_filter: {
                              ...(prev.processing_options.aeo_filter ?? { enabled: false, field: 'custom_labels.custom_label_4', operator: 'equals' as const, value: '' }),
                              retain_published: checked,
                            },
                          }
                        }))}
                      />
                    </div>
                  </div>
                )}

                <Separator className="my-4" />

                <div className="space-y-2">
                  <Label>Max Total Products (Optional)</Label>
                  <p className="text-sm text-muted-foreground">Limit total products processed (pilot + full). Leave empty for no limit.</p>
                  <Input
                    type="number"
                    min={10}
                    max={10000}
                    placeholder="No limit"
                    value={wizard.processing_options.max_total_products || ''}
                    onChange={(e) => setWizard(prev => ({
                      ...prev,
                      processing_options: {
                        ...prev.processing_options,
                        max_total_products: e.target.value ? parseInt(e.target.value) : null
                      }
                    }))}
                    className="w-32"
                  />
                </div>

                <div className="space-y-2">
                  <Label>AI Research for Missing Attributes</Label>
                  <Select
                    value={wizard.processing_options.ai_research}
                    onValueChange={(value) => setWizard(prev => ({
                      ...prev,
                      processing_options: { ...prev.processing_options, ai_research: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mark_for_later">Mark for Later</SelectItem>
                      <SelectItem value="auto_research">Auto Research</SelectItem>
                      <SelectItem value="skip">Skip</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Separator className="my-4" />

                {/* Update Strategy */}
                <h4 className="font-medium">Update Strategy</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Control how data is handled during re-imports
                </p>

                <div className="space-y-2">
                  <Label>Import Mode</Label>
                  <Select
                    value={wizard.processing_options.update_strategy?.mode || 'smart_merge'}
                    onValueChange={(value) => setWizard(prev => ({
                      ...prev,
                      processing_options: {
                        ...prev.processing_options,
                        update_strategy: {
                          ...(prev.processing_options.update_strategy || {}),
                          mode: value
                        }
                      }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="smart_merge">Smart Merge (Recommended)</SelectItem>
                      <SelectItem value="feed_only">Feed Only (Update all feed fields)</SelectItem>
                      <SelectItem value="full_replace">Full Replace (Clean reimport)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {wizard.processing_options.update_strategy?.mode === 'full_replace'
                      ? 'Replace all data from feed (only preserves AI enrichments)'
                      : wizard.processing_options.update_strategy?.mode === 'feed_only'
                        ? 'Update all feed fields, but preserve local modifications'
                        : 'Intelligently merge feed data while preserving local edits and enrichments'}
                  </p>
                </div>

                {wizard.processing_options.update_strategy?.mode !== 'full_replace' && (
                  <div className="ml-4 pl-4 border-l-2 border-muted space-y-3 mt-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Preserve Local Edits</Label>
                        <p className="text-xs text-muted-foreground">Keep manual modifications to product data</p>
                      </div>
                      <Switch
                        checked={wizard.processing_options.update_strategy?.preserve_local_edits ?? true}
                        onCheckedChange={(checked) => setWizard(prev => ({
                          ...prev,
                          processing_options: {
                            ...prev.processing_options,
                            update_strategy: {
                              ...(prev.processing_options.update_strategy || {}),
                              preserve_local_edits: checked
                            }
                          }
                        }))}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Preserve AI Enrichments</Label>
                        <p className="text-xs text-muted-foreground">Keep extracted attributes and benefit scores</p>
                      </div>
                      <Switch
                        checked={wizard.processing_options.update_strategy?.preserve_enrichments ?? true}
                        onCheckedChange={(checked) => setWizard(prev => ({
                          ...prev,
                          processing_options: {
                            ...prev.processing_options,
                            update_strategy: {
                              ...(prev.processing_options.update_strategy || {}),
                              preserve_enrichments: checked
                            }
                          }
                        }))}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Preserve AI Research</Label>
                        <p className="text-xs text-muted-foreground">Keep research results for missing data</p>
                      </div>
                      <Switch
                        checked={wizard.processing_options.update_strategy?.preserve_research ?? true}
                        onCheckedChange={(checked) => setWizard(prev => ({
                          ...prev,
                          processing_options: {
                            ...prev.processing_options,
                            update_strategy: {
                              ...(prev.processing_options.update_strategy || {}),
                              preserve_research: checked
                            }
                          }
                        }))}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Schedule */}
          {wizard.step === 4 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Schedule Frequency</Label>
                <Select
                  value={wizard.schedule_frequency}
                  onValueChange={(value) => setWizard(prev => ({ ...prev, schedule_frequency: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual Only</SelectItem>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {wizard.schedule_frequency !== 'manual' && wizard.schedule_frequency !== 'hourly' && (
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={wizard.schedule_time}
                    onChange={(e) => setWizard(prev => ({ ...prev, schedule_time: e.target.value }))}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Timezone</Label>
                <Select
                  value={wizard.schedule_timezone}
                  onValueChange={(value) => setWizard(prev => ({ ...prev, schedule_timezone: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    <SelectItem value="Europe/London">London</SelectItem>
                    <SelectItem value="Europe/Madrid">Madrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  The feed will be saved in <strong>Paused</strong> status. You can activate it later after reviewing the configuration.
                </AlertDescription>
              </Alert>
            </div>
          )}

          <DialogFooter className="flex justify-between">
            <div>
              {wizard.step > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setWizard(prev => ({ ...prev, step: prev.step - 1 }))}
                >
                  Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowWizard(false)}>
                Cancel
              </Button>
              {wizard.step < 4 ? (
                <Button
                  onClick={() => setWizard(prev => ({ ...prev, step: prev.step + 1 }))}
                  disabled={
                    (wizard.step === 1 && (!wizard.name || !wizard.testResult?.success)) ||
                    (wizard.step === 2 && !wizard.analysis?.success)
                  }
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={() => saveFeedConfig('paused')} disabled={saving}>
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Save (Paused)
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => { setDeleteConfirm(null); setDeletePreview(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Feed Import</DialogTitle>
            <DialogDescription>
              {deletePreview?.config_name ? `"${deletePreview.config_name}"` : 'This feed import configuration'}
            </DialogDescription>
          </DialogHeader>

          {loadingDeletePreview ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : deletePreview ? (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="text-sm font-medium">Associated Data</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Import runs:</span>
                    <span className="font-medium">{deletePreview.import_runs}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Data sources:</span>
                    <span className="font-medium">{deletePreview.data_sources}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Products:</span>
                    <span className="font-medium">{deletePreview.products.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">KB entries:</span>
                    <span className="font-medium">{deletePreview.kb_entries.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {deletePreview.products > 0 || deletePreview.kb_entries > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Choose how to handle the associated product data:
                  </p>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      className="w-full justify-start h-auto py-3"
                      onClick={() => deleteConfirm && deleteFeedConfig(deleteConfirm, false)}
                      disabled={deleting}
                    >
                      <div className="text-left">
                        <div className="font-medium">Delete Config Only</div>
                        <div className="text-xs text-muted-foreground">
                          Keep {deletePreview.products.toLocaleString()} products and {deletePreview.kb_entries.toLocaleString()} KB entries for content generation
                        </div>
                      </div>
                    </Button>
                    <Button
                      variant="destructive"
                      className="w-full justify-start h-auto py-3"
                      onClick={() => deleteConfirm && deleteFeedConfig(deleteConfirm, true)}
                      disabled={deleting}
                    >
                      {deleting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-2" />
                      )}
                      <div className="text-left">
                        <div className="font-medium">Delete Everything</div>
                        <div className="text-xs opacity-80">
                          Remove config, products, and knowledge base entries
                        </div>
                      </div>
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No product data associated with this feed import.
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4">
              Failed to load preview. Please try again.
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteConfirm(null); setDeletePreview(null); }}>
              Cancel
            </Button>
            {deletePreview && deletePreview.products === 0 && deletePreview.kb_entries === 0 && (
              <Button
                variant="destructive"
                onClick={() => deleteConfirm && deleteFeedConfig(deleteConfirm, true)}
                disabled={deleting}
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Progress Dialog */}
      <Dialog open={showProgress} onOpenChange={(open) => {
        if (!open) {
          // Always allow closing - the import continues in the background
          setShowProgress(false);
          // Only clear currentRun if the run is finished (not active)
          if (currentRun && !['pending', 'running', 'enriching', 'embedding', 'aeo_processing', 'processing'].includes(currentRun.status)) {
            setCurrentRun(null);
          }
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {currentRun?.status === 'running' && <Loader2 className="h-5 w-5 animate-spin text-blue-500" />}
              {currentRun?.status === 'enriching' && <Sparkles className="h-5 w-5 animate-pulse text-orange-500" />}
              {currentRun?.status === 'embedding' && <Database className="h-5 w-5 animate-pulse text-cyan-500" />}
              {currentRun?.status === 'aeo_processing' && <Loader2 className="h-5 w-5 animate-spin text-violet-500" />}
              {currentRun?.status === 'processing' && <Loader2 className="h-5 w-5 animate-spin text-purple-500" />}
              {currentRun?.status === 'completed' && <CheckCircle className="h-5 w-5 text-green-500" />}
              {currentRun?.status === 'failed' && <XCircle className="h-5 w-5 text-red-500" />}
              {currentRun?.status === 'cancelled' && <AlertTriangle className="h-5 w-5 text-yellow-500" />}
              Import Progress
            </DialogTitle>
            <DialogDescription>
              {currentRun?.config_name || 'Feed Import'} - {
                currentRun?.status === 'running' ? 'Importing...' :
                currentRun?.status === 'enriching' ? 'AI Enrichment in progress (may take hours)' :
                currentRun?.status === 'embedding' ? 'Generating embeddings (may take minutes)' :
                currentRun?.status === 'aeo_processing' ? 'AEO Pipeline running' :
                currentRun?.status === 'processing' ? 'Processing...' :
                currentRun?.status === 'completed' ? 'Import complete' :
                currentRun?.status === 'failed' ? 'Import failed' :
                currentRun?.status
              }
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {/* Progress Steps */}
            <div className="space-y-3">
              {currentRun?.steps?.map((step, idx) => (
                <div key={step.id} className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {getStepIcon(step)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${step.status === 'pending' ? 'text-muted-foreground' : ''}`}>
                        {getStepLabel(step.step_name)}
                      </span>
                      {getStepDuration(step) && (
                        <span className="text-xs text-muted-foreground">
                          {formatDuration(getStepDuration(step)!)}
                        </span>
                      )}
                    </div>
                    {step.status === 'completed' && step.metrics && (
                      <div className="text-xs text-muted-foreground mt-1 space-y-1">
                        {step.step_name === 'download' && (
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            <span><strong>Size:</strong> {step.metrics.file_size_mb} MB</span>
                            <span><strong>Time:</strong> {formatDuration(step.metrics.duration_ms as number)}</span>
                          </div>
                        )}
                        {step.step_name === 'parse' && (
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            <span><strong>Products:</strong> {(step.metrics.total_products as number)?.toLocaleString()}</span>
                            <span><strong>Categories:</strong> {step.metrics.total_categories}</span>
                            <span><strong>Format:</strong> {(step.metrics.format as string)?.toUpperCase()}</span>
                          </div>
                        )}
                        {step.step_name === 'uniqueness' && (
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            <span className="text-green-600"><strong>New:</strong> {step.metrics.new_products}</span>
                            <span className="text-blue-600"><strong>Updated:</strong> {step.metrics.updated_products}</span>
                            {step.metrics.unavailable_products !== undefined && (step.metrics.unavailable_products as number) > 0 && (
                              <span className="text-orange-600"><strong>Unavailable:</strong> {step.metrics.unavailable_products}</span>
                            )}
                            <span><strong>Unchanged:</strong> {step.metrics.unchanged_products}</span>
                          </div>
                        )}
                        {step.step_name === 'normalization' && (
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            <span className="text-green-600"><strong>Inserted:</strong> {step.metrics.products_inserted}</span>
                            {step.metrics.products_updated !== undefined && (step.metrics.products_updated as number) > 0 && (
                              <span className="text-blue-600"><strong>Updated:</strong> {step.metrics.products_updated}</span>
                            )}
                            <span><strong>Fields:</strong> {step.metrics.fields_normalized}</span>
                            <span><strong>Time:</strong> {formatDuration(step.metrics.duration_ms as number)}</span>
                          </div>
                        )}
                        {step.step_name === 'enrichment' && (
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            <span><strong>Queued:</strong> {step.metrics.products_queued || step.metrics.products_need_enrichment}</span>
                            <span><strong>Mode:</strong> {step.metrics.enrichment_mode}</span>
                          </div>
                        )}
                        {step.step_name === 'enrichment_gate' && (
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {step.metrics.quality_gate_passed !== undefined && (
                              <span className="text-green-600"><strong>Passed:</strong> {step.metrics.quality_gate_passed}</span>
                            )}
                            {step.metrics.quality_score !== undefined && (
                              <span><strong>Quality:</strong> {(step.metrics.quality_score as number)?.toFixed(0)}%</span>
                            )}
                            {step.metrics.note && (
                              <span className="text-muted-foreground italic">{String(step.metrics.note)}</span>
                            )}
                            {step.metrics.skipped && !step.metrics.quality_gate_passed && (
                              <span className="text-muted-foreground italic">{step.metrics.reason}</span>
                            )}
                          </div>
                        )}
                        {step.step_name === 'research' && (
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {step.metrics.researches_initiated !== undefined && (
                              <>
                                <span><strong>Initiated:</strong> {step.metrics.researches_initiated}</span>
                                <span><strong>Successful:</strong> {step.metrics.researches_successful}</span>
                                <span><strong>Success Rate:</strong> {step.metrics.success_rate_percent}%</span>
                              </>
                            )}
                            {step.metrics.skipped && (
                              <span className="text-muted-foreground italic">{step.metrics.reason}</span>
                            )}
                            {step.metrics.mode && (
                              <span><strong>Mode:</strong> {step.metrics.mode}</span>
                            )}
                          </div>
                        )}
                        {step.step_name === 'aeo' && (
                          <div className="space-y-2">
                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                              {step.metrics.kb_imported !== undefined && (
                                <span><strong>KB Entries:</strong> {step.metrics.kb_imported}</span>
                              )}
                              {step.metrics.kb_skipped !== undefined && (
                                <span><strong>Skipped:</strong> {step.metrics.kb_skipped}</span>
                              )}
                              {step.metrics.pilot_category && (
                                <span><strong>Category:</strong> {String(step.metrics.pilot_category).split(' > ').slice(-1)[0]}</span>
                              )}
                            </div>
                            {(step.metrics.aeo_completed || step.metrics.aeo_validated) && (
                              <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {step.metrics.pilot_completed !== undefined && (
                                  <span className="text-blue-600"><strong>Pilot:</strong> {step.metrics.pilot_completed}</span>
                                )}
                                {step.metrics.full_completed !== undefined && (
                                  <span className="text-green-600"><strong>Full:</strong> {step.metrics.full_completed}</span>
                                )}
                                {step.metrics.aeo_validated !== undefined && (
                                  <span className="text-green-600"><strong>Validated:</strong> {step.metrics.aeo_validated}</span>
                                )}
                                {step.metrics.pipeline_status && (
                                  <Badge variant={step.metrics.pipeline_status === 'COMPLETED' ? 'default' : 'secondary'}>
                                    {String(step.metrics.pipeline_status)}
                                  </Badge>
                                )}
                              </div>
                            )}
                            {!step.metrics.aeo_completed && step.metrics.enrichment_batch_id && (
                              <span className="text-orange-600"><strong>Enrichment:</strong> Queued</span>
                            )}
                            {step.metrics.note && (
                              <span className="text-muted-foreground text-xs italic block w-full">{String(step.metrics.note)}</span>
                            )}
                            {step.metrics.skipped && (
                              <span className="text-muted-foreground italic">{step.metrics.reason}</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {step.status === 'failed' && step.error_message && (
                      <p className="text-xs text-red-500 mt-1">{step.error_message}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Sub-batch Progress - shown during async processing phases */}
            {currentRun && ['enriching', 'embedding', 'aeo_processing', 'processing', 'sub_batch_processing'].includes(currentRun.status) && (
              <div className="mt-4">
                <SubBatchProgress runId={currentRun.id} autoRefresh={true} refreshInterval={10000} />
              </div>
            )}

            {/* Summary when enriching (Phase 1 of sequential flow) */}
            {currentRun?.status === 'enriching' && currentRun.summary && (
              <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-950 rounded-lg">
                <h4 className="font-medium text-orange-800 dark:text-orange-200 mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Phase 1/3: AI Enrichment
                </h4>
                <p className="text-sm text-orange-700 dark:text-orange-300 mb-3">
                  Enriching product attributes with AI. This can take hours for large catalogs.
                  <br />
                  <span className="font-medium">Next: Embeddings → AEO Pipeline</span>
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Products:</span>{' '}
                    <span className="font-medium">{currentRun.summary.total_products?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Category:</span>{' '}
                    <span className="font-medium">{currentRun.summary.pilot_category || 'N/A'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Summary when embedding (Phase 2 of sequential flow) */}
            {currentRun?.status === 'embedding' && currentRun.summary && (
              <div className="mt-6 p-4 bg-cyan-50 dark:bg-cyan-950 rounded-lg">
                <h4 className="font-medium text-cyan-800 dark:text-cyan-200 mb-2 flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  Phase 2/3: Generating Embeddings
                </h4>
                <p className="text-sm text-cyan-700 dark:text-cyan-300 mb-3">
                  Creating semantic embeddings from enriched product data.
                  <br />
                  <span className="font-medium">Next: AEO Pipeline</span>
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Products:</span>{' '}
                    <span className="font-medium">{currentRun.summary.total_products?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Category:</span>{' '}
                    <span className="font-medium">{currentRun.summary.pilot_category || 'N/A'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Summary when aeo_processing (Phase 3 of sequential flow) */}
            {currentRun?.status === 'aeo_processing' && currentRun.summary && (
              <div className="mt-6 p-4 bg-violet-50 dark:bg-violet-950 rounded-lg">
                <h4 className="font-medium text-violet-800 dark:text-violet-200 mb-2 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Phase 3/3: AEO Pipeline
                </h4>
                <p className="text-sm text-violet-700 dark:text-violet-300 mb-3">
                  Generating AEO content (questions, answers, structured data).
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Products:</span>{' '}
                    <span className="font-medium">{currentRun.summary.total_products?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Category:</span>{' '}
                    <span className="font-medium">{currentRun.summary.pilot_category || 'N/A'}</span>
                  </div>
                </div>
                {currentRun.pipeline_run_id && (
                  <Button
                    variant="link"
                    className="p-0 h-auto mt-2 text-violet-600"
                    onClick={() => window.location.href = '/pipeline'}
                  >
                    View Pipeline Progress →
                  </Button>
                )}
              </div>
            )}

            {/* Legacy: Summary when processing (old status) */}
            {currentRun?.status === 'processing' && currentRun.summary && (
              <div className="mt-6 p-4 bg-purple-50 dark:bg-purple-950 rounded-lg">
                <h4 className="font-medium text-purple-800 dark:text-purple-200 mb-2">Processing</h4>
                <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">
                  Background processing in progress.
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Products:</span>{' '}
                    <span className="font-medium">{currentRun.summary.total_products?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">New:</span>{' '}
                    <span className="font-medium">{currentRun.summary.new_products?.toLocaleString()}</span>
                  </div>
                </div>
                {currentRun.pipeline_run_id && (
                  <Button
                    variant="link"
                    className="p-0 h-auto mt-2 text-purple-600"
                    onClick={() => window.location.href = '/pipeline'}
                  >
                    View Pipeline Progress →
                  </Button>
                )}
              </div>
            )}

            {/* Summary when completed */}
            {currentRun?.status === 'completed' && currentRun.summary && (
              <div className="mt-6 p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">Import Complete</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Products:</span>{' '}
                    <span className="font-medium">{currentRun.summary.total_products?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">New:</span>{' '}
                    <span className="font-medium">{currentRun.summary.new_products?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Updated:</span>{' '}
                    <span className="font-medium">{currentRun.summary.updated_products?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Duration:</span>{' '}
                    <span className="font-medium">{formatDuration(currentRun.summary.duration_ms)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Error when failed */}
            {currentRun?.status === 'failed' && currentRun.error_message && (
              <Alert variant="destructive" className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{currentRun.error_message}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter className="flex justify-between items-center">
            {/* Zombie warning */}
            {currentRun?.is_zombie && (
              <div className="flex items-center gap-2 text-amber-600 text-sm">
                <AlertTriangle className="h-4 w-4" />
                <span>Stuck: {currentRun.zombie_reason}</span>
              </div>
            )}
            <div className="flex gap-2">
              {['pending', 'running'].includes(currentRun?.status || '') ? (
                <Button variant="outline" onClick={() => currentRun && cancelImport(currentRun.id)}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Import
                </Button>
              ) : ['enriching', 'embedding', 'aeo_processing', 'processing'].includes(currentRun?.status || '') ? (
                <>
                  {currentRun?.is_zombie && (
                    <Button variant="destructive" onClick={() => currentRun && forceCancelImport(currentRun.id, currentRun.config_id)}>
                      <XCircle className="h-4 w-4 mr-2" />
                      Force Cancel
                    </Button>
                  )}
                  <Button onClick={() => { setShowProgress(false); setCurrentRun(null); }}>
                    Close (continues in background)
                  </Button>
                </>
              ) : (
                <Button onClick={() => { setShowProgress(false); setCurrentRun(null); }}>
                  Close
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run History Dialog */}
      <Dialog open={!!historyConfigId} onOpenChange={(open) => {
        if (!open) {
          setHistoryConfigId(null);
          setRunHistory([]);
          setSelectedHistoryRun(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Import Run History
            </DialogTitle>
            <DialogDescription>
              {feedConfigs.find(c => c.id === historyConfigId)?.name} - Past import runs
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {loadingHistory && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}

            {!loadingHistory && runHistory.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No import runs yet</p>
              </div>
            )}

            {!loadingHistory && runHistory.length > 0 && !selectedHistoryRun && (
              <div className="space-y-2">
                {runHistory.map((run) => (
                  <div
                    key={run.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer"
                    onClick={() => viewRunDetails(run.id)}
                  >
                    <div className="flex items-center gap-3">
                      {run.status === 'completed' && <CheckCircle className="h-5 w-5 text-green-500" />}
                      {run.status === 'failed' && <XCircle className="h-5 w-5 text-red-500" />}
                      {run.status === 'cancelled' && <AlertTriangle className="h-5 w-5 text-yellow-500" />}
                      {['running', 'enriching', 'embedding', 'aeo_processing', 'processing'].includes(run.status) && (
                        <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                      )}
                      {run.status === 'pending' && <Clock className="h-5 w-5 text-gray-400" />}
                      <div>
                        <p className="font-medium">
                          {run.started_at ? format(new Date(run.started_at), 'PPp') : 'Pending'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {run.summary?.total_products?.toLocaleString() || 0} products •
                          {run.summary?.new_products?.toLocaleString() || 0} new •
                          {run.summary?.duration_ms ? formatDuration(run.summary.duration_ms) : 'N/A'}
                        </p>
                        {/* Show progress for active runs */}
                        {run.phase_progress && (
                          <div className="flex items-center gap-2 mt-1">
                            <Progress value={run.phase_progress.percentage} className="h-1.5 w-32" />
                            <span className="text-xs font-medium text-orange-600">
                              {run.phase_progress.percentage}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={run.status === 'completed' ? 'default' : run.status === 'failed' ? 'destructive' : 'secondary'}>
                        {run.phase_progress
                          ? `${run.phase_progress.phase_number}/3: ${run.phase_progress.percentage}%`
                          : run.status === 'enriching' ? 'AI Enrichment'
                          : run.status === 'embedding' ? 'Embeddings'
                          : run.status === 'aeo_processing' ? 'AEO Pipeline'
                          : run.status}
                      </Badge>
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Detailed Run View */}
            {selectedHistoryRun && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedHistoryRun(null)}>
                    ← Back to list
                  </Button>
                  {/* Run Options Badges */}
                  <div className="flex items-center gap-2">
                    {selectedHistoryRun.processing_options?.ai_enrichment && (
                      <Badge variant="outline" className="text-xs">AI Enrichment</Badge>
                    )}
                    {selectedHistoryRun.processing_options?.aeo_generation && (
                      <Badge variant="outline" className="text-xs">AEO Generation</Badge>
                    )}
                    {selectedHistoryRun.processing_options?.force_aeo_regeneration && (
                      <Badge variant="destructive" className="text-xs font-semibold">Force AEO Regen</Badge>
                    )}
                    {selectedHistoryRun.processing_options?.force_ai_enrichment && (
                      <Badge variant="destructive" className="text-xs font-semibold">Force Re-Enrich</Badge>
                    )}
                  </div>
                </div>

                {/* Phase Status Banner for active runs with progress */}
                {selectedHistoryRun.status === 'enriching' && (
                  <Alert className="bg-orange-50 border-orange-200 dark:bg-orange-950 dark:border-orange-800">
                    <Sparkles className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-orange-800 dark:text-orange-200">
                      <div className="flex items-center justify-between mb-2">
                        <strong>Phase 1/3: AI Enrichment</strong>
                        {selectedHistoryRun.phase_progress && (
                          <span className="font-mono text-sm">
                            {selectedHistoryRun.phase_progress.processed.toLocaleString()}/{selectedHistoryRun.phase_progress.total.toLocaleString()} ({selectedHistoryRun.phase_progress.percentage}%)
                          </span>
                        )}
                      </div>
                      {selectedHistoryRun.phase_progress && (
                        <Progress value={selectedHistoryRun.phase_progress.percentage} className="h-2 mb-2" />
                      )}
                      <span className="text-sm">Next: Embeddings → AEO Pipeline</span>
                    </AlertDescription>
                  </Alert>
                )}
                {selectedHistoryRun.status === 'embedding' && (
                  <Alert className="bg-cyan-50 border-cyan-200 dark:bg-cyan-950 dark:border-cyan-800">
                    <Database className="h-4 w-4 text-cyan-600" />
                    <AlertDescription className="text-cyan-800 dark:text-cyan-200">
                      <div className="flex items-center justify-between mb-2">
                        <strong>Phase 2/3: Generating Embeddings</strong>
                        {selectedHistoryRun.phase_progress && (
                          <span className="font-mono text-sm">
                            {selectedHistoryRun.phase_progress.processed.toLocaleString()}/{selectedHistoryRun.phase_progress.total.toLocaleString()} ({selectedHistoryRun.phase_progress.percentage}%)
                          </span>
                        )}
                      </div>
                      {selectedHistoryRun.phase_progress && (
                        <Progress value={selectedHistoryRun.phase_progress.percentage} className="h-2 mb-2" />
                      )}
                      <span className="text-sm">Next: AEO Pipeline</span>
                    </AlertDescription>
                  </Alert>
                )}
                {selectedHistoryRun.status === 'aeo_processing' && (
                  <Alert className={selectedHistoryRun.phase_progress?.needs_action
                    ? "bg-amber-50 border-amber-300 dark:bg-amber-950 dark:border-amber-700"
                    : "bg-violet-50 border-violet-200 dark:bg-violet-950 dark:border-violet-800"
                  }>
                    {selectedHistoryRun.phase_progress?.needs_action ? (
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                    ) : (
                      <Loader2 className="h-4 w-4 text-violet-600 animate-spin" />
                    )}
                    <AlertDescription className={selectedHistoryRun.phase_progress?.needs_action
                      ? "text-amber-800 dark:text-amber-200"
                      : "text-violet-800 dark:text-violet-200"
                    }>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <strong>Phase 3/3: AEO Pipeline</strong>
                          {selectedHistoryRun.processing_options?.force_aeo_regeneration && (
                            <Badge variant="destructive" className="text-xs">Force Regen</Badge>
                          )}
                        </div>
                        {selectedHistoryRun.phase_progress && selectedHistoryRun.phase_progress.total > 0 && (
                          <span className="font-mono text-sm">
                            {selectedHistoryRun.phase_progress.processed}/{selectedHistoryRun.phase_progress.total}
                            {selectedHistoryRun.phase_progress.failed > 0 && (
                              <span className="text-red-600 ml-1">({selectedHistoryRun.phase_progress.failed} failed)</span>
                            )}
                          </span>
                        )}
                      </div>
                      {selectedHistoryRun.phase_progress && selectedHistoryRun.phase_progress.total > 0 && (
                        <Progress value={selectedHistoryRun.phase_progress.percentage} className="h-2 mb-2" />
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-sm">{selectedHistoryRun.phase_progress?.status_text || 'Processing AEO content'}</span>
                        {selectedHistoryRun.phase_progress?.pipeline_run_id && (
                          <a
                            href="/pipeline"
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                          >
                            {selectedHistoryRun.phase_progress?.needs_action ? 'Review & Approve →' : 'View Pipeline →'}
                          </a>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="grid grid-cols-4 gap-4 mb-4">
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold">{selectedHistoryRun.summary?.total_products?.toLocaleString() || 0}</div>
                      <div className="text-sm text-muted-foreground">Total Products</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold text-green-600">{selectedHistoryRun.summary?.new_products?.toLocaleString() || 0}</div>
                      <div className="text-sm text-muted-foreground">New</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">{selectedHistoryRun.summary?.updated_products?.toLocaleString() || 0}</div>
                      <div className="text-sm text-muted-foreground">Updated</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold">
                        {/* For completed runs, prefer total_duration_ms (includes async phases) */}
                        {selectedHistoryRun.status === 'completed' && selectedHistoryRun.summary?.total_duration_ms
                          ? formatDuration(selectedHistoryRun.summary.total_duration_ms)
                          : selectedHistoryRun.summary?.duration_ms
                            ? formatDuration(selectedHistoryRun.summary.duration_ms)
                            : 'N/A'}
                      </div>
                      <div className="text-sm text-muted-foreground">Processing Time</div>
                      {/* Show sync time breakdown for completed runs with total duration */}
                      {selectedHistoryRun.status === 'completed' && selectedHistoryRun.summary?.total_duration_ms && selectedHistoryRun.summary?.duration_ms && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {formatDuration(selectedHistoryRun.summary.duration_ms)} sync
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <Separator />

                <h4 className="font-semibold">Processing Steps</h4>
                <div className="space-y-3">
                  {selectedHistoryRun.steps?.map((step) => (
                    <div key={step.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {getStepIcon(step)}
                          <span className="font-medium">{getStepLabel(step.step_name)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStepDuration(step) && (
                            <span className="text-sm text-muted-foreground">
                              {formatDuration(getStepDuration(step)!)}
                            </span>
                          )}
                          <Badge variant={step.status === 'completed' ? 'default' : step.status === 'failed' ? 'destructive' : 'secondary'}>
                            {step.status}
                          </Badge>
                        </div>
                      </div>

                      {step.metrics && (
                        <div className="mt-2 p-3 bg-muted/50 rounded-md text-sm">
                          {step.step_name === 'download' && (
                            <div className="grid grid-cols-3 gap-4">
                              <div><strong>File Size:</strong> {step.metrics.file_size_mb} MB</div>
                              <div><strong>Download Time:</strong> {formatDuration(step.metrics.duration_ms as number)}</div>
                              {step.metrics.started_at && <div><strong>Started:</strong> {format(new Date(step.metrics.started_at as string), 'HH:mm:ss')}</div>}
                            </div>
                          )}
                          {step.step_name === 'parse' && (
                            <div className="grid grid-cols-4 gap-4">
                              <div><strong>Products:</strong> {(step.metrics.total_products as number)?.toLocaleString()}</div>
                              <div><strong>Categories:</strong> {step.metrics.total_categories}</div>
                              <div><strong>Format:</strong> {(step.metrics.format as string)?.toUpperCase()}</div>
                              <div><strong>Parse Time:</strong> {formatDuration(step.metrics.duration_ms as number)}</div>
                            </div>
                          )}
                          {step.step_name === 'uniqueness' && (
                            <div className="grid grid-cols-4 gap-4">
                              <div className="text-green-600"><strong>New:</strong> {step.metrics.new_products}</div>
                              <div className="text-blue-600"><strong>Updated:</strong> {step.metrics.updated_products}</div>
                              <div className="text-orange-600"><strong>Unavailable:</strong> {step.metrics.unavailable_products || 0}</div>
                              <div><strong>Unchanged:</strong> {step.metrics.unchanged_products}</div>
                            </div>
                          )}
                          {step.step_name === 'normalization' && (
                            <div className="grid grid-cols-4 gap-4">
                              <div className="text-green-600"><strong>Inserted:</strong> {step.metrics.products_inserted}</div>
                              <div className="text-blue-600"><strong>Updated:</strong> {step.metrics.products_updated}</div>
                              <div><strong>Fields:</strong> {step.metrics.fields_normalized}</div>
                              <div><strong>Time:</strong> {formatDuration(step.metrics.duration_ms as number)}</div>
                            </div>
                          )}
                          {step.step_name === 'enrichment' && (
                            <div className="space-y-2">
                              <div className="grid grid-cols-3 gap-4">
                                <div><strong>Products Queued:</strong> {step.metrics.products_queued || step.metrics.products_need_enrichment}</div>
                                <div><strong>Mode:</strong> {step.metrics.enrichment_mode}</div>
                                <div><strong>Completion:</strong> {step.metrics.completion || step.metrics.enrichment_percentage || 0}%</div>
                              </div>
                              {step.metrics.products_enriched && (
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="text-green-600"><strong>Enriched:</strong> {step.metrics.products_enriched}</div>
                                  {step.metrics.products_failed !== undefined && <div className="text-red-600"><strong>Failed:</strong> {step.metrics.products_failed}</div>}
                                </div>
                              )}
                            </div>
                          )}
                          {step.step_name === 'research' && (
                            <div className="grid grid-cols-4 gap-4">
                              <div><strong>Mode:</strong> {step.metrics.mode}</div>
                              <div><strong>Initiated:</strong> {step.metrics.researches_initiated}</div>
                              <div><strong>Successful:</strong> {step.metrics.researches_successful}</div>
                              <div><strong>Success Rate:</strong> {step.metrics.success_rate_percent}%</div>
                            </div>
                          )}
                          {step.step_name === 'enrichment_gate' && (
                            <div className="grid grid-cols-3 gap-4">
                              {step.metrics.quality_gate_passed !== undefined && (
                                <div className="text-green-600"><strong>Passed:</strong> {step.metrics.quality_gate_passed}</div>
                              )}
                              {step.metrics.quality_score !== undefined && (
                                <div><strong>Quality:</strong> {(step.metrics.quality_score as number)?.toFixed(0)}%</div>
                              )}
                              {step.metrics.note && (
                                <div className="col-span-3 text-muted-foreground text-sm italic">{String(step.metrics.note)}</div>
                              )}
                              {step.metrics.skipped && !step.metrics.quality_gate_passed && (
                                <div className="col-span-3 text-muted-foreground italic">{step.metrics.reason}</div>
                              )}
                            </div>
                          )}
                          {step.step_name === 'aeo' && (
                            <div className="space-y-2">
                              <div className="grid grid-cols-3 gap-4">
                                <div><strong>KB Entries:</strong> {step.metrics.kb_imported || 0}</div>
                                <div><strong>Skipped:</strong> {step.metrics.kb_skipped || 0}</div>
                                <div><strong>Category:</strong> {step.metrics.pilot_category ? String(step.metrics.pilot_category).split(' > ').slice(-1)[0] : 'N/A'}</div>
                              </div>
                              {(step.metrics.aeo_completed || step.metrics.aeo_validated) && (
                                <div className="grid grid-cols-4 gap-4">
                                  {step.metrics.pilot_completed !== undefined && (
                                    <div className="text-blue-600"><strong>Pilot:</strong> {step.metrics.pilot_completed}</div>
                                  )}
                                  {step.metrics.full_completed !== undefined && (
                                    <div className="text-green-600"><strong>Full:</strong> {step.metrics.full_completed}</div>
                                  )}
                                  {step.metrics.aeo_validated !== undefined && (
                                    <div className="text-green-600"><strong>Validated:</strong> {step.metrics.aeo_validated}</div>
                                  )}
                                  {step.metrics.pipeline_status && (
                                    <div>
                                      <Badge variant={step.metrics.pipeline_status === 'COMPLETED' ? 'default' : 'secondary'}>
                                        {String(step.metrics.pipeline_status)}
                                      </Badge>
                                    </div>
                                  )}
                                </div>
                              )}
                              {!step.metrics.aeo_completed && step.metrics.enrichment_batch_id && (
                                <div><strong>Enrichment:</strong> <span className="text-orange-600">Queued</span></div>
                              )}
                              {step.metrics.note && (
                                <div className="text-muted-foreground text-sm italic">{String(step.metrics.note)}</div>
                              )}
                            </div>
                          )}
                          {step.metrics.skipped && step.step_name !== 'enrichment_gate' && (
                            <div className="text-muted-foreground italic">{step.metrics.reason}</div>
                          )}
                        </div>
                      )}

                      {step.error_message && (
                        <div className="mt-2 p-3 bg-red-50 dark:bg-red-950 rounded-md text-sm text-red-600">
                          <strong>Error:</strong> {step.error_message}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-between items-center">
            {/* Zombie warning for selected history run */}
            {selectedHistoryRun?.is_zombie && (
              <div className="flex items-center gap-2 text-amber-600 text-sm">
                <AlertTriangle className="h-4 w-4" />
                <span>Stuck: {selectedHistoryRun.zombie_reason} ({selectedHistoryRun.zombie_minutes} min)</span>
              </div>
            )}
            <div className="flex gap-2">
              {/* Force Cancel button for zombie runs in history */}
              {selectedHistoryRun && ['enriching', 'embedding', 'aeo_processing', 'processing'].includes(selectedHistoryRun.status) && selectedHistoryRun.is_zombie && (
                <Button
                  variant="destructive"
                  onClick={() => forceCancelImport(selectedHistoryRun.id, selectedHistoryRun.config_id)}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Force Cancel
                </Button>
              )}
              <Button onClick={() => { setHistoryConfigId(null); setRunHistory([]); setSelectedHistoryRun(null); }}>
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Feed Config Dialog */}
      <Dialog open={!!editingConfig} onOpenChange={(open) => {
        if (!open) {
          setEditingConfig(null);
          setEditForm({});
          setEditCategoryAnalysis(null);
        }
      }}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Edit Feed Import
            </DialogTitle>
            <DialogDescription>
              Modify the feed import configuration
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="font-medium">Basic Information</h4>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Name</Label>
                  <Input
                    id="edit-name"
                    value={editForm.name || ''}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="My Feed Import"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-url">Feed URL</Label>
                  <Input
                    id="edit-url"
                    value={editForm.feed_url || ''}
                    onChange={(e) => setEditForm({ ...editForm, feed_url: e.target.value })}
                    placeholder="https://example.com/feed.tsv"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-format">Feed Format</Label>
                  <Select
                    value={editForm.feed_format || 'auto'}
                    onValueChange={(value) => setEditForm({ ...editForm, feed_format: value })}
                  >
                    <SelectTrigger id="edit-format">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto-detect</SelectItem>
                      <SelectItem value="tsv">TSV (Tab-separated)</SelectItem>
                      <SelectItem value="csv">CSV (Comma-separated)</SelectItem>
                      <SelectItem value="xml">XML (Google Shopping)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Schedule */}
            <div className="space-y-4">
              <h4 className="font-medium">Schedule</h4>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-frequency">Frequency</Label>
                  <Select
                    value={editForm.schedule_frequency || 'manual'}
                    onValueChange={(value) => setEditForm({ ...editForm, schedule_frequency: value })}
                  >
                    <SelectTrigger id="edit-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual only</SelectItem>
                      <SelectItem value="hourly">Hourly</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editForm.schedule_frequency && editForm.schedule_frequency !== 'manual' && editForm.schedule_frequency !== 'hourly' && (
                  <div className="space-y-2">
                    <Label htmlFor="edit-time">Time (UTC)</Label>
                    <Input
                      id="edit-time"
                      type="time"
                      value={editForm.schedule_time || '03:00'}
                      onChange={(e) => setEditForm({ ...editForm, schedule_time: e.target.value })}
                    />
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Processing Options */}
            <div className="space-y-4">
              <h4 className="font-medium">Processing Options</h4>

              {/* Quick Presets */}
              <div className="flex gap-2 pb-2 border-b">
                <Button
                  variant={(editForm.processing_options?.aeo_generation ?? true) ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEditForm({
                    ...editForm,
                    processing_options: {
                      ...editForm.processing_options,
                      auto_normalize: true,
                      ai_enrichment: true,
                      aeo_generation: true
                    }
                  })}
                >
                  Full Processing
                </Button>
                <Button
                  variant={!(editForm.processing_options?.aeo_generation ?? true) && (editForm.processing_options?.ai_enrichment ?? true) ? "default" : "outline"}
                  size="sm"
                  onClick={() => setEditForm({
                    ...editForm,
                    processing_options: {
                      ...editForm.processing_options,
                      auto_normalize: true,
                      ai_enrichment: true,
                      aeo_generation: false
                    }
                  })}
                >
                  Catalog Only
                </Button>
              </div>
              {!(editForm.processing_options?.aeo_generation ?? true) && (
                <Alert className="bg-blue-50 dark:bg-blue-950/30 border-blue-200">
                  <Database className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Catalog Only Mode:</strong> Products will be imported and enriched, but no AEO content will be generated.
                    Use this to build your product catalog for category research before running content generation.
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Auto-normalize fields</Label>
                    <p className="text-xs text-muted-foreground">Standardize product data fields</p>
                  </div>
                  <Switch
                    checked={editForm.processing_options?.auto_normalize ?? true}
                    onCheckedChange={(checked) => setEditForm({
                      ...editForm,
                      processing_options: { ...editForm.processing_options, auto_normalize: checked }
                    })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>AI Enrichment</Label>
                    <p className="text-xs text-muted-foreground">Extract additional fields from descriptions</p>
                  </div>
                  <Switch
                    checked={editForm.processing_options?.ai_enrichment ?? true}
                    onCheckedChange={(checked) => setEditForm({
                      ...editForm,
                      processing_options: { ...editForm.processing_options, ai_enrichment: checked }
                    })}
                  />
                </div>

                {/* Force AI Re-Enrichment - one-shot flag */}
                {(editForm.processing_options?.ai_enrichment ?? true) && (
                  <div className="ml-4 pl-4 border-l-2 border-orange-200 dark:border-orange-800">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="flex items-center gap-2">
                          Force AI Re-Enrichment
                          <Badge variant="warning" className="text-xs">One-shot</Badge>
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Re-enrich all products on next run, even if already enriched (auto-resets after completion)
                        </p>
                      </div>
                      <Switch
                        checked={editForm.processing_options?.force_ai_enrichment ?? false}
                        onCheckedChange={(checked) => setEditForm({
                          ...editForm,
                          processing_options: { ...editForm.processing_options, force_ai_enrichment: checked }
                        })}
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>AEO Generation</Label>
                    <p className="text-xs text-muted-foreground">Generate optimized content via pipeline</p>
                  </div>
                  <Switch
                    checked={editForm.processing_options?.aeo_generation ?? true}
                    onCheckedChange={(checked) => setEditForm({
                      ...editForm,
                      processing_options: { ...editForm.processing_options, aeo_generation: checked }
                    })}
                  />
                </div>

                {/* Force AEO regeneration - one-shot flag */}
                {(editForm.processing_options?.aeo_generation ?? true) && (
                  <div className="ml-4 pl-4 border-l-2 border-orange-200 dark:border-orange-800">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="flex items-center gap-2">
                          Force AEO Regeneration
                          <Badge variant="warning" className="text-xs">One-shot</Badge>
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Regenerate all AEO content on next run (auto-resets after completion)
                        </p>
                      </div>
                      <Switch
                        checked={editForm.processing_options?.force_aeo_regeneration ?? false}
                        onCheckedChange={(checked) => setEditForm({
                          ...editForm,
                          processing_options: { ...editForm.processing_options, force_aeo_regeneration: checked }
                        })}
                      />
                    </div>
                  </div>
                )}

                {/* Auto-approve pilot settings - only shown when AEO generation is enabled */}
                {(editForm.processing_options?.aeo_generation ?? true) && (
                  <div className="ml-4 pl-4 border-l-2 border-muted space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Auto-approve Pilot</Label>
                        <p className="text-xs text-muted-foreground">Auto-proceed to full generation if pilot passes</p>
                      </div>
                      <Switch
                        checked={editForm.processing_options?.auto_approve_pilot ?? false}
                        onCheckedChange={(checked) => setEditForm({
                          ...editForm,
                          processing_options: { ...editForm.processing_options, auto_approve_pilot: checked }
                        })}
                      />
                    </div>

                    {(editForm.processing_options?.auto_approve_pilot ?? false) && (
                      <div className="space-y-1">
                        <Label>Success Threshold (%)</Label>
                        <Input
                          type="number"
                          min={50}
                          max={100}
                          value={editForm.processing_options?.auto_approve_threshold ?? 80}
                          onChange={(e) => setEditForm({
                            ...editForm,
                            processing_options: { ...editForm.processing_options, auto_approve_threshold: parseInt(e.target.value) || 80 }
                          })}
                          className="w-24"
                        />
                      </div>
                    )}

                    {/* AEO Product Filter */}
                    <Separator className="my-2" />
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Filter Products for AEO</Label>
                        <p className="text-xs text-muted-foreground">Only generate AEO for matching products</p>
                      </div>
                      <Switch
                        checked={editForm.processing_options?.aeo_filter?.enabled ?? false}
                        onCheckedChange={(checked) => setEditForm({
                          ...editForm,
                          processing_options: {
                            ...editForm.processing_options,
                            aeo_filter: {
                              ...(editForm.processing_options?.aeo_filter ?? { field: 'custom_labels.custom_label_4', operator: 'equals' as const, value: '' }),
                              enabled: checked,
                            },
                          }
                        })}
                      />
                    </div>

                    {editForm.processing_options?.aeo_filter?.enabled && (
                      <div className="space-y-2 mt-2 p-2 bg-muted/30 rounded-md">
                        <div className="space-y-1">
                          <Label className="text-xs">Field</Label>
                          <Select
                            value={editForm.processing_options.aeo_filter.field}
                            onValueChange={(value) => setEditForm({
                              ...editForm,
                              processing_options: {
                                ...editForm.processing_options,
                                aeo_filter: { ...editForm.processing_options.aeo_filter!, field: value }
                              }
                            })}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="custom_labels.custom_label_0">Custom Label 0</SelectItem>
                              <SelectItem value="custom_labels.custom_label_1">Custom Label 1</SelectItem>
                              <SelectItem value="custom_labels.custom_label_2">Custom Label 2</SelectItem>
                              <SelectItem value="custom_labels.custom_label_3">Custom Label 3</SelectItem>
                              <SelectItem value="custom_labels.custom_label_4">Custom Label 4</SelectItem>
                              <SelectItem value="brand">Brand</SelectItem>
                              <SelectItem value="category">Category</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Operator</Label>
                          <Select
                            value={editForm.processing_options.aeo_filter.operator}
                            onValueChange={(value: 'equals' | 'not_equals' | 'in' | 'exists') => setEditForm({
                              ...editForm,
                              processing_options: {
                                ...editForm.processing_options,
                                aeo_filter: { ...editForm.processing_options.aeo_filter!, operator: value }
                              }
                            })}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="equals">Equals</SelectItem>
                              <SelectItem value="not_equals">Not Equals</SelectItem>
                              <SelectItem value="in">In List</SelectItem>
                              <SelectItem value="exists">Exists (not empty)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {editForm.processing_options.aeo_filter.operator !== 'exists' && (
                          <div className="space-y-1">
                            <Label className="text-xs">{editForm.processing_options.aeo_filter.operator === 'in' ? 'Values (comma-separated)' : 'Value'}</Label>
                            <Input
                              className="h-8"
                              placeholder={editForm.processing_options.aeo_filter.operator === 'in' ? '2, 3, 4' : '2'}
                              value={editForm.processing_options.aeo_filter.operator === 'in'
                                ? (editForm.processing_options.aeo_filter.values || []).join(', ')
                                : editForm.processing_options.aeo_filter.value || ''
                              }
                              onChange={(e) => {
                                const aeoFilter = editForm.processing_options?.aeo_filter!;
                                if (aeoFilter.operator === 'in') {
                                  // Don't filter empty values while typing - allows comma to be typed
                                  setEditForm({
                                    ...editForm,
                                    processing_options: {
                                      ...editForm.processing_options,
                                      aeo_filter: { ...aeoFilter, values: e.target.value.split(',').map(v => v.trim()) }
                                    }
                                  });
                                } else {
                                  setEditForm({
                                    ...editForm,
                                    processing_options: {
                                      ...editForm.processing_options,
                                      aeo_filter: { ...aeoFilter, value: e.target.value }
                                    }
                                  });
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Retain Published — peer to the filter master toggle, not gated by it */}
                    <Separator className="my-2" />
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Retain published products</Label>
                        <p className="text-xs text-muted-foreground">
                          Keep products eligible for AEO once they appear in the publisher manifest, even if they no longer match the filter values. Prevents drop-off when categorization changes upstream.
                        </p>
                      </div>
                      <Switch
                        checked={editForm.processing_options?.aeo_filter?.retain_published ?? false}
                        onCheckedChange={(checked) => setEditForm({
                          ...editForm,
                          processing_options: {
                            ...editForm.processing_options,
                            aeo_filter: {
                              ...(editForm.processing_options?.aeo_filter ?? { enabled: false, field: 'custom_labels.custom_label_4', operator: 'equals' as const, value: '' }),
                              retain_published: checked,
                            },
                          }
                        })}
                      />
                    </div>
                  </div>
                )}

                <Separator className="my-3" />

                {/* Pipeline Optimization - Cost Saving Options */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h5 className="text-sm font-medium">Pipeline Optimization</h5>
                    <Badge variant="secondary" className="text-xs">Cost Savings</Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Content Validation</Label>
                      <p className="text-xs text-muted-foreground">Run 5 LLM validators (hallucination, persona, completeness). Saves ~50% LLM cost if disabled.</p>
                    </div>
                    <Switch
                      checked={editForm.processing_options?.enable_validation ?? false}
                      onCheckedChange={(checked) => setEditForm({
                        ...editForm,
                        processing_options: { ...editForm.processing_options, enable_validation: checked }
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Quality Gate</Label>
                      <p className="text-xs text-muted-foreground">Skip products missing critical attributes (saves generation cost)</p>
                    </div>
                    <Switch
                      checked={editForm.processing_options?.enable_quality_gate ?? true}
                      onCheckedChange={(checked) => setEditForm({
                        ...editForm,
                        processing_options: { ...editForm.processing_options, enable_quality_gate: checked }
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Auto-approve All Content</Label>
                      <p className="text-xs text-muted-foreground">Automatically publish all generated content (skips manual review)</p>
                    </div>
                    <Switch
                      checked={editForm.processing_options?.auto_approve_all ?? false}
                      onCheckedChange={(checked) => setEditForm({
                        ...editForm,
                        processing_options: {
                          ...editForm.processing_options,
                          auto_approve_all: checked,
                          // Clear threshold if auto_approve_all is enabled
                          auto_approve_content_threshold: checked ? null : editForm.processing_options?.auto_approve_content_threshold
                        }
                      })}
                    />
                  </div>

                  {/* Score-based auto-approval - only when validation is enabled and auto_approve_all is false */}
                  {(editForm.processing_options?.enable_validation ?? false) && !(editForm.processing_options?.auto_approve_all ?? false) && (
                    <div className="ml-4 pl-4 border-l-2 border-muted space-y-2">
                      <div className="space-y-1">
                        <Label>Auto-approve Threshold (Score)</Label>
                        <p className="text-xs text-muted-foreground">Auto-publish content with validation score ≥ threshold (1-10). Leave empty to disable.</p>
                        <Input
                          type="number"
                          min={1}
                          max={10}
                          placeholder="Disabled"
                          value={editForm.processing_options?.auto_approve_content_threshold ?? ''}
                          onChange={(e) => setEditForm({
                            ...editForm,
                            processing_options: {
                              ...editForm.processing_options,
                              auto_approve_content_threshold: e.target.value ? parseInt(e.target.value) : null
                            }
                          })}
                          className="w-24"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <Separator className="my-3" />

                <div className="space-y-1">
                  <Label>Max Total Products</Label>
                  <p className="text-xs text-muted-foreground">Limit total products (pilot + full). Empty = no limit.</p>
                  <Input
                    type="number"
                    min={10}
                    max={10000}
                    placeholder="No limit"
                    value={editForm.processing_options?.max_total_products || ''}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      processing_options: {
                        ...editForm.processing_options,
                        max_total_products: e.target.value ? parseInt(e.target.value) : null
                      }
                    })}
                    className="w-32"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Update Strategy */}
            <div className="space-y-4">
              <h4 className="font-medium">Update Strategy</h4>
              <p className="text-sm text-muted-foreground">
                Control how data is handled during re-imports
              </p>

              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Import Mode</Label>
                  <Select
                    value={editForm.processing_options?.update_strategy?.mode || 'smart_merge'}
                    onValueChange={(value) => setEditForm({
                      ...editForm,
                      processing_options: {
                        ...editForm.processing_options,
                        update_strategy: {
                          ...(editForm.processing_options?.update_strategy || {}),
                          mode: value
                        }
                      }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="smart_merge">Smart Merge (Recommended)</SelectItem>
                      <SelectItem value="feed_only">Feed Only (Update all feed fields)</SelectItem>
                      <SelectItem value="full_replace">Full Replace (Clean reimport)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {editForm.processing_options?.update_strategy?.mode === 'full_replace'
                      ? 'Replace all data from feed (only preserves AI enrichments)'
                      : editForm.processing_options?.update_strategy?.mode === 'feed_only'
                        ? 'Update all feed fields, but preserve local modifications'
                        : 'Intelligently merge feed data while preserving local edits and enrichments'}
                  </p>
                </div>

                {editForm.processing_options?.update_strategy?.mode !== 'full_replace' && (
                  <div className="ml-4 pl-4 border-l-2 border-muted space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Preserve Local Edits</Label>
                        <p className="text-xs text-muted-foreground">Keep manual modifications to product data</p>
                      </div>
                      <Switch
                        checked={editForm.processing_options?.update_strategy?.preserve_local_edits ?? true}
                        onCheckedChange={(checked) => setEditForm({
                          ...editForm,
                          processing_options: {
                            ...editForm.processing_options,
                            update_strategy: {
                              ...(editForm.processing_options?.update_strategy || {}),
                              preserve_local_edits: checked
                            }
                          }
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Preserve AI Enrichments</Label>
                        <p className="text-xs text-muted-foreground">Keep extracted attributes and benefit scores</p>
                      </div>
                      <Switch
                        checked={editForm.processing_options?.update_strategy?.preserve_enrichments ?? true}
                        onCheckedChange={(checked) => setEditForm({
                          ...editForm,
                          processing_options: {
                            ...editForm.processing_options,
                            update_strategy: {
                              ...(editForm.processing_options?.update_strategy || {}),
                              preserve_enrichments: checked
                            }
                          }
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Preserve AI Research</Label>
                        <p className="text-xs text-muted-foreground">Keep research results for missing data</p>
                      </div>
                      <Switch
                        checked={editForm.processing_options?.update_strategy?.preserve_research ?? true}
                        onCheckedChange={(checked) => setEditForm({
                          ...editForm,
                          processing_options: {
                            ...editForm.processing_options,
                            update_strategy: {
                              ...(editForm.processing_options?.update_strategy || {}),
                              preserve_research: checked
                            }
                          }
                        })}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Category Selection */}
            <Separator />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Selected Categories</h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={analyzeEditFeed}
                  disabled={editCategoryAnalysis?.loading}
                >
                  {editCategoryAnalysis?.loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Analyze Feed
                    </>
                  )}
                </Button>
              </div>

              {/* Current selected categories - clickable to remove */}
              {(editForm.selected_categories?.length || 0) > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {editForm.selected_categories?.map((cat, idx) => (
                    <Badge
                      key={idx}
                      variant="secondary"
                      className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground max-w-full"
                      title={cat}
                      onClick={() => setEditForm({
                        ...editForm,
                        selected_categories: editForm.selected_categories?.filter(c => c !== cat)
                      })}
                    >
                      <span className="truncate">{cat}</span> ×
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No categories selected (all products will be imported)</p>
              )}

              {/* Available categories from analysis */}
              {editCategoryAnalysis?.categories && editCategoryAnalysis.categories.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium">Available categories to add:</p>
                  <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                    {editCategoryAnalysis.categories
                      .filter(cat => !editForm.selected_categories?.includes(cat.path))
                      .map((cat, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 p-1 hover:bg-muted rounded cursor-pointer min-w-0"
                          title={cat.path}
                          onClick={() => setEditForm({
                            ...editForm,
                            selected_categories: [...(editForm.selected_categories || []), cat.path]
                          })}
                        >
                          <span className="text-sm truncate">{cat.path}</span>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {cat.product_count} products
                          </span>
                        </div>
                      ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Click a category to add it. Click × on selected categories to remove them.
                  </p>
                </div>
              )}

              {editCategoryAnalysis?.error && (
                <p className="text-sm text-destructive">{editCategoryAnalysis.error}</p>
              )}
            </div>

            {/* Excluded Attributes */}
            <Separator />
            <div className="space-y-2">
              <h4 className="font-medium">Excluded Attributes</h4>
              <p className="text-xs text-muted-foreground">
                Attributes to exclude from import (e.g., package_weight, internal_sku)
              </p>
              <div className="flex flex-wrap gap-2">
                {(editForm.attribute_config?.excluded_attributes || []).map((attr, idx) => (
                  <Badge
                    key={idx}
                    variant="destructive"
                    className="cursor-pointer"
                    onClick={() => setEditForm({
                      ...editForm,
                      attribute_config: {
                        ...editForm.attribute_config,
                        excluded_attributes: (editForm.attribute_config?.excluded_attributes || []).filter(a => a !== attr)
                      }
                    })}
                  >
                    {attr} ×
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Attribute name to exclude"
                  id="new-excluded-attr"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const input = e.target as HTMLInputElement;
                      const value = input.value.trim();
                      if (value && !(editForm.attribute_config?.excluded_attributes || []).includes(value)) {
                        setEditForm({
                          ...editForm,
                          attribute_config: {
                            ...editForm.attribute_config,
                            excluded_attributes: [...(editForm.attribute_config?.excluded_attributes || []), value]
                          }
                        });
                        input.value = '';
                      }
                    }
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const input = document.getElementById('new-excluded-attr') as HTMLInputElement;
                    const value = input?.value.trim();
                    if (value && !(editForm.attribute_config?.excluded_attributes || []).includes(value)) {
                      setEditForm({
                        ...editForm,
                        attribute_config: {
                          ...editForm.attribute_config,
                          excluded_attributes: [...(editForm.attribute_config?.excluded_attributes || []), value]
                        }
                      });
                      input.value = '';
                    }
                  }}
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Notifications */}
            <Separator />
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Notifications
              </h4>
              <p className="text-xs text-muted-foreground">
                Email addresses to notify when this feed import completes
              </p>
              <div className="flex flex-wrap gap-2">
                {(editForm.notification_emails || []).map((email, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="cursor-pointer"
                    onClick={() => setEditForm({
                      ...editForm,
                      notification_emails: (editForm.notification_emails || []).filter(e => e !== email)
                    })}
                  >
                    {email} ×
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="email@example.com"
                  type="email"
                  id="new-notification-email"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const input = e.target as HTMLInputElement;
                      const value = input.value.trim().toLowerCase();
                      if (value && value.includes('@') && !(editForm.notification_emails || []).includes(value)) {
                        setEditForm({
                          ...editForm,
                          notification_emails: [...(editForm.notification_emails || []), value]
                        });
                        input.value = '';
                      }
                    }
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const input = document.getElementById('new-notification-email') as HTMLInputElement;
                    const value = input?.value.trim().toLowerCase();
                    if (value && value.includes('@') && !(editForm.notification_emails || []).includes(value)) {
                      setEditForm({
                        ...editForm,
                        notification_emails: [...(editForm.notification_emails || []), value]
                      });
                      input.value = '';
                    }
                  }}
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Sub-batch Size */}
            <Separator />
            <div className="space-y-2">
              <h4 className="font-medium">Sub-batch Size</h4>
              <p className="text-xs text-muted-foreground">
                Number of products per processing batch. Smaller batches show progress faster but have more overhead.
                Minimum 20 for batch API cost savings (50% discount).
              </p>
              <Input
                type="number"
                min={20}
                max={200}
                value={editForm.sub_batch_size || 50}
                onChange={(e) => setEditForm({
                  ...editForm,
                  sub_batch_size: Math.max(20, parseInt(e.target.value) || 50)
                })}
                className="w-24"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingConfig(null); setEditForm({}); }}>
              Cancel
            </Button>
            <Button onClick={saveConfigEdits} disabled={savingEdit}>
              {savingEdit ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Preview Dialog */}
      <Dialog open={!!previewConfigId} onOpenChange={(open) => {
        if (!open) {
          setPreviewConfigId(null);
          setImportPreview(null);
        }
      }}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Import Preview
            </DialogTitle>
            <DialogDescription>
              Preview what will happen when you run the import
            </DialogDescription>
          </DialogHeader>

          {loadingPreview && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground">Analyzing feed changes...</p>
            </div>
          )}

          {!loadingPreview && importPreview && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold">{importPreview.preview.total_products.toLocaleString()}</div>
                    <div className="text-sm text-muted-foreground">Total Products</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{importPreview.preview.new_products.toLocaleString()}</div>
                    <div className="text-sm text-muted-foreground">New</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{importPreview.preview.updated_products.toLocaleString()}</div>
                    <div className="text-sm text-muted-foreground">Updated</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{importPreview.preview.enrichments_preserved.toLocaleString()}</div>
                    <div className="text-sm text-muted-foreground">Enrichments Kept</div>
                  </CardContent>
                </Card>
              </div>

              {/* Feed Misalignment Alerts */}
              {(importPreview.preview.unavailable_products > 0 || importPreview.preview.needs_regeneration > 0) && (
                <div className="space-y-3">
                  {importPreview.preview.unavailable_products > 0 && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>{importPreview.preview.unavailable_products.toLocaleString()}</strong> products will be marked as <strong>unavailable</strong> (removed from feed).
                        They will be hidden from Content Review and exports.
                        {importPreview.preview.unavailable_products_sample?.length > 0 && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-xs hover:underline">Show examples</summary>
                            <ul className="mt-1 text-xs space-y-1 pl-4">
                              {importPreview.preview.unavailable_products_sample.map((p, i) => (
                                <li key={i} className="font-mono">{p.product_id}: {p.product_name?.substring(0, 50)}</li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}
                  {importPreview.preview.needs_regeneration > 0 && (
                    <Alert>
                      <RefreshCw className="h-4 w-4" />
                      <AlertDescription>
                        <strong>{importPreview.preview.needs_regeneration.toLocaleString()}</strong> products have key field changes and may need <strong>content regeneration</strong>.
                        {importPreview.preview.regeneration_sample?.length > 0 && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-xs hover:underline">Show examples</summary>
                            <ul className="mt-1 text-xs space-y-1 pl-4">
                              {importPreview.preview.regeneration_sample.map((p, i) => (
                                <li key={i}>
                                  <span className="font-mono">{p.product_id}</span>: changed {p.changed_fields?.join(', ')}
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {/* Preservation Summary */}
              {(importPreview.preview.local_modifications_preserved > 0 || importPreview.preview.enrichments_preserved > 0) && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    <strong>{importPreview.preview.local_modifications_preserved}</strong> local modifications and{' '}
                    <strong>{importPreview.preview.enrichments_preserved}</strong> AI enrichments will be preserved.
                  </AlertDescription>
                </Alert>
              )}

              {/* Strategy Applied */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">Strategy Applied</h4>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">
                    {importPreview.strategy_applied.mode === 'smart_merge' ? 'Smart Merge' :
                     importPreview.strategy_applied.mode === 'feed_only' ? 'Feed Only' : 'Full Replace'}
                  </Badge>
                  {importPreview.strategy_applied.preserve_local_edits && (
                    <Badge variant="secondary">Preserve Local Edits</Badge>
                  )}
                  {importPreview.strategy_applied.preserve_enrichments && (
                    <Badge variant="secondary">Preserve Enrichments</Badge>
                  )}
                  {importPreview.strategy_applied.preserve_research && (
                    <Badge variant="secondary">Preserve Research</Badge>
                  )}
                </div>
              </div>

              {/* Category Readiness Table */}
              {importPreview.categories && importPreview.categories.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium">Category Readiness</h4>
                  {importPreview.aeo_filter_active && (
                    <p className="text-xs text-muted-foreground">AEO filter is active — "Eligible" columns show only products matching the filter criteria.</p>
                  )}
                  <div className="border rounded-lg overflow-auto" style={{ maxHeight: '400px' }}>
                    <table className="w-full text-xs whitespace-nowrap">
                      <thead className="bg-muted/50 sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 font-medium sticky left-0 bg-muted/50 z-20" rowSpan={2}>Category</th>
                          <th className="text-center p-1 font-medium border-l" colSpan={5}>Products</th>
                          <th className="text-center p-1 font-medium border-l" colSpan={2}>Enrichment</th>
                          <th className="text-center p-1 font-medium border-l" colSpan={3}>Semantic Gaps</th>
                          <th className="text-center p-1 font-medium border-l" colSpan={3}>AEO Generation</th>
                        </tr>
                        <tr className="text-[10px] text-muted-foreground">
                          <th className="p-1 border-l">Feed</th>
                          <th className="p-1">Catalog</th>
                          <th className="p-1 text-green-600">New</th>
                          <th className="p-1 text-red-600">Removed</th>
                          <th className="p-1 text-blue-600">Updated</th>
                          <th className="p-1 border-l">Catalog</th>
                          <th className="p-1 text-purple-600">Kept</th>
                          <th className="p-1 border-l text-green-600">Applied</th>
                          <th className="p-1 text-yellow-600">Pending</th>
                          <th className="p-1 text-orange-600">Expert</th>
                          <th className="p-1 border-l">Eligible</th>
                          <th className="p-1">Generated</th>
                          <th className="p-1">Coverage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.categories.map((cat) => {
                          const eligibleTotal = importPreview.aeo_filter_active ? cat.aeo_eligible_catalog : cat.in_catalog;
                          const coveragePct = eligibleTotal > 0
                            ? Math.round((cat.aeo_eligible_with_generation / eligibleTotal) * 100)
                            : cat.in_catalog === 0 ? 0 : null;
                          return (
                            <tr key={cat.category} className="border-t hover:bg-muted/30">
                              <td className="p-2 font-medium sticky left-0 bg-background" title={cat.category}>
                                {cat.category.includes('>') ? cat.category.split('>').pop()?.trim() : cat.category}
                              </td>
                              <td className="p-1 text-right border-l">{cat.in_feed.toLocaleString()}</td>
                              <td className="p-1 text-right">{cat.in_catalog.toLocaleString()}</td>
                              <td className="p-1 text-right text-green-600">{cat.new_in_feed > 0 ? `+${cat.new_in_feed.toLocaleString()}` : '-'}</td>
                              <td className="p-1 text-right text-red-600">{cat.missing_from_feed > 0 ? `-${cat.missing_from_feed.toLocaleString()}` : '-'}</td>
                              <td className="p-1 text-right text-blue-600">{cat.updated > 0 ? cat.updated.toLocaleString() : '-'}</td>
                              <td className="p-1 text-right border-l">{cat.enriched_catalog.toLocaleString()}</td>
                              <td className="p-1 text-right text-purple-600">{cat.enrichment_kept > 0 ? cat.enrichment_kept.toLocaleString() : '-'}</td>
                              <td className="p-1 text-right border-l">
                                {cat.gaps_analyzed ? (
                                  <span className="text-green-600">{cat.gaps_applied.toLocaleString()}</span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="p-1 text-right">
                                {cat.gaps_pending > 0 ? (
                                  <span className="text-yellow-600">{cat.gaps_pending}</span>
                                ) : cat.gaps_analyzed ? (
                                  <CheckCircle className="h-3 w-3 text-green-500 inline" />
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="p-1 text-right">
                                {cat.gaps_needs_expert > 0 ? (
                                  <span className="text-orange-600">{cat.gaps_needs_expert}</span>
                                ) : cat.gaps_analyzed ? (
                                  <CheckCircle className="h-3 w-3 text-green-500 inline" />
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="p-1 text-right border-l">{eligibleTotal.toLocaleString()}</td>
                              <td className="p-1 text-right">{cat.aeo_generated_catalog.toLocaleString()}</td>
                              <td className="p-1 text-right">
                                {coveragePct !== null ? (
                                  <span className={coveragePct >= 80 ? 'text-green-600' : coveragePct >= 40 ? 'text-yellow-600' : 'text-red-600'}>
                                    {coveragePct}%
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      {importPreview.totals && (
                        <tfoot className="bg-muted/50 font-medium border-t-2 sticky bottom-0">
                          <tr>
                            <td className="p-2 sticky left-0 bg-muted/50">TOTAL ({importPreview.categories.length} categories)</td>
                            <td className="p-1 text-right border-l">{importPreview.totals.in_feed.toLocaleString()}</td>
                            <td className="p-1 text-right">{importPreview.totals.in_catalog.toLocaleString()}</td>
                            <td className="p-1 text-right text-green-600">{importPreview.totals.new_in_feed > 0 ? `+${importPreview.totals.new_in_feed.toLocaleString()}` : '-'}</td>
                            <td className="p-1 text-right text-red-600">{importPreview.totals.missing_from_feed > 0 ? `-${importPreview.totals.missing_from_feed.toLocaleString()}` : '-'}</td>
                            <td className="p-1 text-right text-blue-600">{importPreview.totals.updated.toLocaleString()}</td>
                            <td className="p-1 text-right border-l">{importPreview.totals.enriched_catalog.toLocaleString()}</td>
                            <td className="p-1 text-right text-purple-600">{importPreview.totals.enrichment_kept.toLocaleString()}</td>
                            <td className="p-1 text-right border-l text-green-600">{importPreview.totals.gaps_applied.toLocaleString()}</td>
                            <td className="p-1 text-right text-yellow-600">{importPreview.totals.gaps_pending > 0 ? importPreview.totals.gaps_pending.toLocaleString() : '-'}</td>
                            <td className="p-1 text-right text-orange-600">{importPreview.totals.gaps_needs_expert > 0 ? importPreview.totals.gaps_needs_expert.toLocaleString() : '-'}</td>
                            {(() => {
                              const t = importPreview.totals;
                              const eligTotal = importPreview.aeo_filter_active ? t.aeo_eligible_catalog : t.in_catalog;
                              const covPct = eligTotal > 0 ? Math.round((t.aeo_eligible_with_generation / eligTotal) * 100) : 0;
                              return (
                                <>
                                  <td className="p-1 text-right border-l">{eligTotal.toLocaleString()}</td>
                                  <td className="p-1 text-right">{t.aeo_generated_catalog.toLocaleString()}</td>
                                  <td className="p-1 text-right">
                                    <span className={covPct >= 80 ? 'text-green-600' : covPct >= 40 ? 'text-yellow-600' : 'text-red-600'}>
                                      {covPct}%
                                    </span>
                                  </td>
                                </>
                              );
                            })()}
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              )}

              {/* Changes by Field */}
              {Object.keys(importPreview.preview.changes_by_field).length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium">Changes by Field</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-2">Field</th>
                          <th className="text-right p-2">Updated</th>
                          <th className="text-right p-2">Preserved</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(importPreview.preview.changes_by_field).map(([field, counts]) => (
                          <tr key={field} className="border-t">
                            <td className="p-2 font-mono text-xs">{field}</td>
                            <td className="p-2 text-right text-blue-600">{counts.updated.toLocaleString()}</td>
                            <td className="p-2 text-right text-gray-500">{counts.preserved.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Sample Changes */}
              {importPreview.preview.sample_changes.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium">Sample Changes</h4>
                  <ScrollArea className="h-48 border rounded-lg p-2">
                    {importPreview.preview.sample_changes.map((sample, idx) => (
                      <div key={idx} className="mb-4 p-3 bg-muted/30 rounded">
                        <p className="font-medium text-sm">{sample.product_name}</p>
                        <p className="text-xs text-muted-foreground mb-2">ID: {sample.product_id}</p>
                        <div className="space-y-1">
                          {sample.changes.map((change, cIdx) => (
                            <div key={cIdx} className="flex items-center gap-2 text-xs">
                              <span className="font-mono">{change.field}:</span>
                              <Badge variant={change.action === 'update' ? 'default' : 'secondary'} className="text-xs">
                                {change.action}
                              </Badge>
                              {change.reason && <span className="text-muted-foreground">({change.reason})</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Preview generated in {importPreview.duration_ms}ms at {new Date(importPreview.generated_at).toLocaleString()}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setPreviewConfigId(null); setImportPreview(null); }}>
              Close
            </Button>
            {importPreview && previewConfigId && (
              <Button
                onClick={() => {
                  const configId = previewConfigId;
                  setPreviewConfigId(null);
                  setImportPreview(null);
                  triggerImport(configId);
                }}
                disabled={triggering !== null}
              >
                {triggering ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Proceed with Import
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
